import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useDevice } from '../../context/DeviceContext';
import { toast } from '../Toast';
import ChevronIcon from '../ChevronIcon';
import {
  MacroRecordsHeader,
  MacroRecordsList,
} from '../MacroEditor/MacroRecords';
import {
  KEY_FUNCTION_PANEL_ORDER_KEY,
  readPanelOrder,
  writePanelOrder,
} from '../../utils/layoutPreferences';
import {
  getMouseVisual,
  MOUSE_BUTTON_LABELS,
} from '../../assets/mice/MouseVisualRegistry';
import {
  MACRO_AXIS_MAX,
  MACRO_AXIS_MIN,
  MACRO_EVENT_TYPE_MOUSE_BUTTON,
  MACRO_EVENT_TYPE_MOUSE_MOVE,
  MACRO_EVENT_TYPE_MOUSE_X,
  MACRO_EVENT_TYPE_MOUSE_Y,
  MACRO_EVENT_TYPE_NAMES,
  MACRO_EVENT_TYPE_WHEEL,
  MACRO_MOUSE_BUTTON_BACK,
  MACRO_MOUSE_BUTTON_FORWARD,
  MACRO_MOUSE_BUTTON_LEFT,
  MACRO_MOUSE_BUTTON_MIDDLE,
  MACRO_MOUSE_BUTTON_OPTIONS,
  MACRO_MOUSE_BUTTON_RIGHT,
  MACRO_MOUSE_MOVE_DELAY_MS,
  MACRO_MOUSE_MOVE_X_MAX,
  MACRO_MOUSE_MOVE_X_MIN,
  MACRO_MOUSE_MOVE_Y_MAX,
  MACRO_MOUSE_MOVE_Y_MIN,
  clampMacroAxisValue,
  clampMacroLoopCount,
  clampMacroMouseButtons,
  clampMacroMoveX,
  clampMacroMoveY,
  decodeMacroAxisValue,
  encodeMacroAxisValue,
  encodeMacroBindingValue,
  encodeNewMacroBinding,
  formatMacroAxisLabel,
  formatMacroMouseButtons,
  formatMacroMoveLabel,
  isMacroAxisEventType,
  isMacroBindingValue,
  macroLoopLivesInMacroObject,
  macroLoopModeFromType,
  macroLoopModeUsesCount,
  macroLoopTypeFromMode,
  macroLoopTypeUsesCount,
  parseMacroBindingValue,
  supportsMacroMouseMove,
  MACRO_BINDING_TYPE_LEGACY,
  MACRO_LOOP_COUNT_MAX,
  MACRO_LOOP_COUNT_MIN,
  MACRO_LOOP_TYPE_ONCE,
} from '../../utils/MacroEvents';
import './KeyFunction.css';

const LOCAL_MACRO_STORAGE_KEY = 'mouse-driver.macros.localEnvironment';

const getAppLayerTarget = () => {
  if (typeof document === 'undefined') return null;
  return document.querySelector('.app') || document.body;
};

const renderMacroEditorLayer = (editor, embedded) => {
  if (embedded || typeof document === 'undefined') return editor;

  // 按键分配中的编辑器挂到应用根层，既保留主题样式，又避开分配面板的
  // 尺寸、overflow 和层叠上下文限制。
  const portalTarget = getAppLayerTarget();
  return portalTarget ? createPortal(editor, portalTarget) : editor;
};

const renderFloatingMenu = (menu) => {
  const portalTarget = getAppLayerTarget();
  return portalTarget ? createPortal(menu, portalTarget) : menu;
};

// 添加/插入事件菜单里的选项键 -> 协议事件类型（类型 5/6/7 共用同一种记录）
const AXIS_RECORD_EVENT_TYPES = {
  wheel: MACRO_EVENT_TYPE_WHEEL,
  mouseX: MACRO_EVENT_TYPE_MOUSE_X,
  mouseY: MACRO_EVENT_TYPE_MOUSE_Y,
};

/**
 * “添加事件”按钮和右键“向上/向下插入”共用同一份菜单分类：
 * 键盘事件、鼠标移动、鼠标事件（含滚轮）、间隔延时。
 * key 与 addMacroRecord / insertRecord 的 type 参数一一对应。
 * needsMouseMove 的项仅在设备协议支持鼠标移动事件（V3.0+）时显示。
 */
const MACRO_EVENT_MENU_GROUPS = [
  {
    title: '键盘事件',
    items: [
      { key: 'down', label: '按下' },
      { key: 'up', label: '抬起' },
    ],
  },
  {
    title: '鼠标移动',
    items: [
      { key: 'move', label: '鼠标移动', needsMouseMove: true },
      { key: 'mouseX', label: '鼠标X' },
      { key: 'mouseY', label: '鼠标Y' },
    ],
  },
  {
    title: '鼠标事件',
    items: [
      { key: 'mouse', label: '鼠标按键' },
      { key: 'wheel', label: '滚轮' },
    ],
  },
  {
    title: '间隔延时',
    items: [
      { key: 'delay', label: '延时' },
    ],
  },
];

// 事件菜单是浮层，祖先 .macro-editor-body 会滚动裁剪，因此统一用固定定位并收进视口。
const FLOATING_MENU_GAP = 4; // 浮层与锚点的间距
const FLOATING_MENU_EDGE = 8; // 浮层与视口边缘的最小留白
const FLOATING_MENU_MIN_HEIGHT = 140; // 视口很矮时至少留出可滚动的高度

const clampToViewport = (value, size, limit) =>
  Math.max(FLOATING_MENU_EDGE, Math.min(value, limit - FLOATING_MENU_EDGE - size));

const styleCreatesFixedContainingBlock = (style) => {
  const none = (value) => Boolean(value) && value !== 'none';
  return (
    none(style.transform) ||
    none(style.filter) ||
    none(style.perspective) ||
    none(style.backdropFilter) ||
    none(style.webkitBackdropFilter) ||
    /(?:^|,)\s*(?:transform|filter|perspective|backdrop-filter)\s*(?:,|$)/.test(
      style.willChange || ''
    )
  );
};

/*
 * 主题 2 的 .macro-editor-dialog 有 backdrop-filter，会让 position:fixed
 * 相对对话框而不是视口。getBoundingClientRect 仍是视口坐标，必须先扣掉
 * 这个包含块的偏移，弹窗才会落在点击处附近。
 */
const getFixedContainingBlockOrigin = (el) => {
  let node = el.parentElement;
  while (node && node !== document.documentElement) {
    if (styleCreatesFixedContainingBlock(getComputedStyle(node))) {
      const rect = node.getBoundingClientRect();
      return { x: rect.left, y: rect.top };
    }
    node = node.parentElement;
  }
  return { x: 0, y: 0 };
};

const viewportToFixed = (el, viewportX, viewportY) => {
  const origin = getFixedContainingBlockOrigin(el);
  return {
    left: viewportX - origin.x,
    top: viewportY - origin.y,
  };
};

// 完整内容高度：scrollHeight 不含边框，且限高后也不变，重排时测量结果稳定
const naturalMenuHeight = (menu) =>
  menu.scrollHeight + menu.offsetHeight - menu.clientHeight;

/**
 * 三点菜单默认向下展开；下方不够且上方更宽裕时翻到按钮上面，
 * 左右超出视口则平移回来。主题 1 工作区/对话框 overflow 裁剪不到
 * 挂在应用根层的浮层，但窄/矮窗口仍要把菜单收进视口。
 */
const fitMenuBelowAnchor = (anchor, menuWidth, menuHeight) => {
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const spaceBelow = viewportHeight - anchor.bottom - FLOATING_MENU_GAP - FLOATING_MENU_EDGE;
  const spaceAbove = anchor.top - FLOATING_MENU_GAP - FLOATING_MENU_EDGE;
  const openDown = menuHeight <= spaceBelow || spaceBelow >= spaceAbove;
  const room = Math.max(openDown ? spaceBelow : spaceAbove, 72);
  const maxHeight = menuHeight > 0 ? Math.min(menuHeight, room) : room;
  const top = openDown
    ? anchor.bottom + FLOATING_MENU_GAP
    : anchor.top - FLOATING_MENU_GAP - maxHeight;
  return {
    left: clampToViewport(anchor.left, menuWidth, viewportWidth),
    top: clampToViewport(top, maxHeight, viewportHeight),
    maxHeight,
  };
};

/**
 * 把菜单收进视口：沿用原来向上展开的样子，上方放不下且下方更宽裕时翻到按钮下面，
 * 两侧都不够就限高由菜单自身滚动，保证所有事件都点得到。
 */
const fitMenuAboveAnchor = (anchor, menuHeight) => {
  const viewportHeight = window.innerHeight;
  const spaceAbove = anchor.top - FLOATING_MENU_GAP - FLOATING_MENU_EDGE;
  const spaceBelow = viewportHeight - anchor.bottom - FLOATING_MENU_GAP - FLOATING_MENU_EDGE;
  const openUp = menuHeight <= spaceAbove || spaceAbove >= spaceBelow;
  const room = Math.max(openUp ? spaceAbove : spaceBelow, FLOATING_MENU_MIN_HEIGHT);
  const maxHeight = menuHeight > 0 ? Math.min(menuHeight, room) : room;
  const top = openUp
    ? anchor.top - FLOATING_MENU_GAP - maxHeight
    : anchor.bottom + FLOATING_MENU_GAP;
  return { top: clampToViewport(top, maxHeight, viewportHeight), maxHeight };
};

// 过滤掉当前设备不支持的事件，顺带丢弃因此变空的分类
const visibleMacroEventGroups = (canUseMouseMove) =>
  MACRO_EVENT_MENU_GROUPS
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => !item.needsMouseMove || canUseMouseMove),
    }))
    .filter((group) => group.items.length > 0);

// 旧版本编辑器把鼠标按键存成 1-5 的序号，而协议 Byte1 是位掩码；
// 滚轮则是独立的 'scroll' 记录。读取本地宏时统一迁移到协议口径。
const LEGACY_MOUSE_ORDINAL_TO_MASK = {
  1: MACRO_MOUSE_BUTTON_LEFT,
  2: MACRO_MOUSE_BUTTON_RIGHT,
  3: MACRO_MOUSE_BUTTON_MIDDLE,
  4: MACRO_MOUSE_BUTTON_BACK,
  5: MACRO_MOUSE_BUTTON_FORWARD,
};

const migrateStoredRecord = (record) => {
  if (!record || typeof record !== 'object') return record;

  // 滚轮 / 鼠标X / 鼠标Y 统一为 axis 记录，值按补码还原成有符号数
  if (record.type === 'scroll' || record.type === 'axis' || isMacroAxisEventType(record.eventType)) {
    const eventType = isMacroAxisEventType(record.eventType)
      ? Number(record.eventType)
      : MACRO_EVENT_TYPE_WHEEL;
    const value = decodeMacroAxisValue(record.value ?? record.keyCode ?? 0);
    return {
      ...record,
      type: 'axis',
      eventType,
      keyCode: null,
      value,
      keyName: formatMacroAxisLabel(eventType, value),
      action: 'down',
    };
  }

  // valueScheme 标记已按位掩码存储，避免重复迁移把掩码当成序号再转一次
  if (record.type === 'mouse' && record.valueScheme !== 'mask') {
    const ordinal = record.value ?? record.keyCode ?? 1;
    const mask = LEGACY_MOUSE_ORDINAL_TO_MASK[ordinal] ?? clampMacroMouseButtons(ordinal);
    return {
      ...record,
      valueScheme: 'mask',
      eventType: MACRO_EVENT_TYPE_MOUSE_BUTTON,
      keyCode: mask,
      value: mask,
      keyName: formatMacroMouseButtons(mask),
    };
  }

  return record;
};

const uniqueCloneName = (baseName, existingNames) => {
  const used = new Set(existingNames);
  const stem = `${baseName || '宏'} 副本`;
  if (!used.has(stem)) return stem;
  let index = 2;
  while (used.has(`${stem} ${index}`)) index += 1;
  return `${stem} ${index}`;
};

const cloneMacroRecords = (records) =>
  (Array.isArray(records) ? records : []).map((record, index) => ({
    ...record,
    id: `clone-${Date.now()}-${index}`,
  }));

const loadLocalMacros = () => {
  try {
    const raw = localStorage.getItem(LOCAL_MACRO_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    return parsed.map((macro, index) => ({
      ...macro,
      id: macro.id || `local-${Date.now()}-${index}`,
      source: 'local',
      fromDevice: false,
      macroIndex: undefined,
      records: Array.isArray(macro.records) ? macro.records.map(migrateStoredRecord) : [],
    }));
  } catch (error) {
    console.warn('读取本地宏失败:', error);
    return [];
  }
};

// 基础按键映射表
const KEY_MAP_BASIC = {
  '0x0100F000': '左键',
  '0x0100F100': '右键',
  '0x0100F200': '中键',
  '0x0100F300': '前侧键',
  '0x0100F400': '后侧键',
  '0x0100F500': '向左滚动',
  '0x0100F600': '向右滚动',
  '0x0100F801': '向上滚动',
  '0x0100F8FF': '向下滚动',
  '0x0100F900': '左键双击',
};

// DPI 按键映射表
const KEY_MAP_DPI = {
  '0x07000100': 'DPI+',
  '0x07000200': 'DPI-',
  '0x07000300': 'DPI循环',
};

// 配置文件按键映射表
const KEY_MAP_PROFILE = {
  '0x08000100': '下一个配置文件',
  '0x08000200': '上一个配置文件',
  '0x08000400': '配置文件 1',
  '0x08000401': '配置文件 2',
  '0x08000402': '配置文件 3',
  '0x08000403': '配置文件 4',
  '0x08000404': '配置文件 5',
};

// 快捷键映射表 - 通用
const KEY_MAP_SHORTCUT_COMMON = {
  '0x03006F00': '屏幕亮度增加',
  '0x03007000': '屏幕亮度减少',
};

// 快捷键映射表 - Mac (Cmd = 0x08)
const KEY_MAP_SHORTCUT_MAC = {
  '0x00040600': '复制',      // Cmd+C
  '0x00041B00': '剪切',      // Cmd+X
  '0x00041900': '粘贴',      // Cmd+V
  '0x03009F02': '调度中心',  // Mission Control (Consumer 0x029F)
  '0x00015200': '调度中心2',
  '0x0300A002': '启动台',    // Launchpad (Consumer 0x02A0)
};

// 快捷键映射表 - Windows (Ctrl = 0x01, Win = 0x08)
const KEY_MAP_SHORTCUT_WIN = {
  '0x00010600': '复制',      // Ctrl+C
  '0x00011B00': '剪切',      // Ctrl+X
  '0x00011900': '粘贴',      // Ctrl+V
  '0x00080700': '显示桌面',  // Win+D
};

// 多媒体键映射表
const KEY_MAP_MULTIMEDIA = {
  '0x0300E900': '音量+',
  '0x0300EA00': '音量-',
  '0x0300E200': '静音',
  '0x0300CD00': '播放/暂停',
  '0x0300B600': '上一曲',
  '0x0300B500': '下一曲',
  '0x03006F00': '亮度+',
  '0x03007000': '亮度-',
};

// 禁用
const KEY_MAP_DISABLE = {
  '0x00000000': '禁用',
};

// 命令列表展示：左侧为实际按键/组合，右侧为用途。
const COMMAND_PRESENTATION = {
  '0X0100F000': { key: 'Mouse 1', usage: '执行鼠标左键单击' },
  '0X0100F100': { key: 'Mouse 2', usage: '执行鼠标右键单击' },
  '0X0100F200': { key: 'Mouse 3', usage: '执行鼠标中键单击' },
  '0X0100F300': { key: 'Mouse 4', usage: '执行鼠标前进操作' },
  '0X0100F400': { key: 'Mouse 5', usage: '执行鼠标后退操作' },
  '0X0100F500': { key: '滚轮左', usage: '横向向左滚动' },
  '0X0100F600': { key: '滚轮右', usage: '横向向右滚动' },
  '0X0100F801': { key: '滚轮上', usage: '纵向向上滚动' },
  '0X0100F8FF': { key: '滚轮下', usage: '纵向向下滚动' },
  '0X0100F900': { key: '左键 ×2', usage: '执行鼠标左键双击' },
  '0X07000100': { key: 'DPI +', usage: '切换到更高 DPI 档位' },
  '0X07000200': { key: 'DPI −', usage: '切换到更低 DPI 档位' },
  '0X07000300': { key: 'DPI 循环', usage: '循环切换 DPI 档位' },
  '0X08000100': { key: 'Profile +', usage: '切换到下一个配置文件' },
  '0X08000200': { key: 'Profile −', usage: '切换到上一个配置文件' },
  '0X08000400': { key: 'Profile 1', usage: '切换到配置文件 1' },
  '0X08000401': { key: 'Profile 2', usage: '切换到配置文件 2' },
  '0X08000402': { key: 'Profile 3', usage: '切换到配置文件 3' },
  '0X08000403': { key: 'Profile 4', usage: '切换到配置文件 4' },
  '0X08000404': { key: 'Profile 5', usage: '切换到配置文件 5' },
  '0X00040600': { key: '⌘ + C', usage: '复制所选内容' },
  '0X00041B00': { key: '⌘ + X', usage: '剪切所选内容' },
  '0X00041900': { key: '⌘ + V', usage: '粘贴剪贴板内容' },
  '0X03009F02': { key: 'Mission Control', usage: '打开调度中心' },
  '0X00015200': { key: 'Ctrl + ↑', usage: '打开调度中心' },
  '0X0300A002': { key: 'Launchpad', usage: '打开启动台' },
  '0X00010600': { key: 'Ctrl + C', usage: '复制所选内容' },
  '0X00011B00': { key: 'Ctrl + X', usage: '剪切所选内容' },
  '0X00011900': { key: 'Ctrl + V', usage: '粘贴剪贴板内容' },
  '0X00080700': { key: 'Win + D', usage: '显示或隐藏桌面' },
  '0X03006F00': { key: '亮度 +', usage: '提高屏幕亮度' },
  '0X03007000': { key: '亮度 −', usage: '降低屏幕亮度' },
  '0X0300E900': { key: '音量 +', usage: '提高系统音量' },
  '0X0300EA00': { key: '音量 −', usage: '降低系统音量' },
  '0X0300E200': { key: '静音', usage: '开启或关闭系统静音' },
  '0X0300CD00': { key: '播放 / 暂停', usage: '播放或暂停媒体' },
  '0X0300B600': { key: '上一曲', usage: '切换到上一首媒体' },
  '0X0300B500': { key: '下一曲', usage: '切换到下一首媒体' },
  '0X00000000': { key: '禁用', usage: '关闭所选鼠标按键功能' },
};

const createCommand = ([value, label], usageSuffix = '') => {
  const presentation = COMMAND_PRESENTATION[String(value).toUpperCase()] || {};
  const displayLabel = `${label}${usageSuffix}`;
  return {
    value,
    label: displayLabel,
    keyLabel: presentation.key || label,
    usage: displayLabel,
  };
};

// 修饰键定义
const MODIFIER_KEYS = [
  { id: 'ctrl', label: 'Control', bit: 0x01 },
  { id: 'shift', label: 'Shift', bit: 0x02 },
  { id: 'alt', label: 'Alt/Option', bit: 0x04 },
  { id: 'win', label: 'Win/Command', bit: 0x08 },
];

// 键码映射 (HID Usage ID)
/** 完整键盘键码列表（HID Usage）：数字/符号/字母/功能键均完整展示 */
const KEY_CODES = [
  // 功能 / 编辑
  { label: 'Esc', code: 0x29 },
  { label: 'Tab', code: 0x2B },
  { label: 'Caps', code: 0x39 },
  { label: 'Enter', code: 0x28 },
  { label: 'Bksp', code: 0x2A },
  { label: 'Space', code: 0x2C },
  // 数字 0-9（主键盘）
  { label: '1', code: 0x1E },
  { label: '2', code: 0x1F },
  { label: '3', code: 0x20 },
  { label: '4', code: 0x21 },
  { label: '5', code: 0x22 },
  { label: '6', code: 0x23 },
  { label: '7', code: 0x24 },
  { label: '8', code: 0x25 },
  { label: '9', code: 0x26 },
  { label: '0', code: 0x27 },
  // 符号（- . ; 等，单独清晰显示）
  { label: '-', code: 0x2D },
  { label: '=', code: 0x2E },
  { label: '`', code: 0x35 },
  { label: '[', code: 0x2F },
  { label: ']', code: 0x30 },
  { label: '\\', code: 0x31 },
  { label: ';', code: 0x33 },
  { label: "'", code: 0x34 },
  { label: ',', code: 0x36 },
  { label: '.', code: 0x37 },
  { label: '/', code: 0x38 },
  // 字母 A-Z
  { label: 'A', code: 0x04 },
  { label: 'B', code: 0x05 },
  { label: 'C', code: 0x06 },
  { label: 'D', code: 0x07 },
  { label: 'E', code: 0x08 },
  { label: 'F', code: 0x09 },
  { label: 'G', code: 0x0A },
  { label: 'H', code: 0x0B },
  { label: 'I', code: 0x0C },
  { label: 'J', code: 0x0D },
  { label: 'K', code: 0x0E },
  { label: 'L', code: 0x0F },
  { label: 'M', code: 0x10 },
  { label: 'N', code: 0x11 },
  { label: 'O', code: 0x12 },
  { label: 'P', code: 0x13 },
  { label: 'Q', code: 0x14 },
  { label: 'R', code: 0x15 },
  { label: 'S', code: 0x16 },
  { label: 'T', code: 0x17 },
  { label: 'U', code: 0x18 },
  { label: 'V', code: 0x19 },
  { label: 'W', code: 0x1A },
  { label: 'X', code: 0x1B },
  { label: 'Y', code: 0x1C },
  { label: 'Z', code: 0x1D },
  // F1-F12
  { label: 'F1', code: 0x3A },
  { label: 'F2', code: 0x3B },
  { label: 'F3', code: 0x3C },
  { label: 'F4', code: 0x3D },
  { label: 'F5', code: 0x3E },
  { label: 'F6', code: 0x3F },
  { label: 'F7', code: 0x40 },
  { label: 'F8', code: 0x41 },
  { label: 'F9', code: 0x42 },
  { label: 'F10', code: 0x43 },
  { label: 'F11', code: 0x44 },
  { label: 'F12', code: 0x45 },
  // 导航区 / 特殊
  { label: 'PrtSc', code: 0x46, small: true },
  { label: 'ScrLk', code: 0x47, small: true },
  { label: 'Pause', code: 0x48, small: true },
  { label: 'Ins', code: 0x49 },
  { label: 'Del', code: 0x4C },
  { label: 'Home', code: 0x4A },
  { label: 'End', code: 0x4D },
  { label: 'PgUp', code: 0x4B },
  { label: 'PgDn', code: 0x4E },
  // 方向
  { label: 'Up', code: 0x52 },
  { label: 'Down', code: 0x51 },
  { label: 'Left', code: 0x50 },
  { label: 'Right', code: 0x4F },
  // 小键盘
  { label: 'NumLk', code: 0x53 },
  { label: 'N/', code: 0x54 },
  { label: 'N*', code: 0x55 },
  { label: 'N-', code: 0x56 },
  { label: 'N+', code: 0x57 },
  { label: 'N.', code: 0x63 },
  { label: 'N0', code: 0x62 },
  { label: 'N1', code: 0x59 },
  { label: 'N2', code: 0x5A },
  { label: 'N3', code: 0x5B },
  { label: 'N4', code: 0x5C },
  { label: 'N5', code: 0x5D },
  { label: 'N6', code: 0x5E },
  { label: 'N7', code: 0x5F },
  { label: 'N8', code: 0x60 },
  { label: 'N9', code: 0x61 },
  { label: 'NEnt', code: 0x58 },
  { label: 'N=', code: 0x67 },
  // 修饰键
  { label: 'LShift', code: 0xE1 },
  { label: 'RShift', code: 0xE5 },
  { label: 'LCtrl', code: 0xE0 },
  { label: 'RCtrl', code: 0xE4 },
  { label: 'LAlt', code: 0xE2 },
  { label: 'RAlt', code: 0xE6 },
  { label: 'LWin', code: 0xE3 },
  { label: 'RWin', code: 0xE7 },
];

// 合并所有映射表用于显示当前值
const KEY_MAP = {
  ...KEY_MAP_BASIC,
  ...KEY_MAP_SHORTCUT_COMMON,
  ...KEY_MAP_SHORTCUT_MAC,
  ...KEY_MAP_SHORTCUT_WIN,
  ...KEY_MAP_DPI,
  ...KEY_MAP_PROFILE,
  ...KEY_MAP_MULTIMEDIA,
  ...KEY_MAP_DISABLE,
};

/** 命令目录：一级分类 + 子命令（用于纵向树列表） */
const COMMAND_CATEGORIES = [
  {
    id: 'basic',
    label: '基础按键',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_BASIC).map((entry) => createCommand(entry)),
  },
  {
    id: 'edit',
    label: '编辑',
    kind: 'commands',
    // 编辑类快捷键：同时列出 Mac/Win 编码，名称区分平台
    commands: [
      ...Object.entries(KEY_MAP_SHORTCUT_MAC)
        .filter(([, label]) => ['复制', '剪切', '粘贴'].includes(label))
        .map((entry) => createCommand(entry, ' (Mac)')),
      ...Object.entries(KEY_MAP_SHORTCUT_WIN)
        .filter(([, label]) => ['复制', '剪切', '粘贴'].includes(label))
        .map((entry) => createCommand(entry, ' (Windows)')),
    ],
  },
  {
    id: 'mac',
    label: 'Mac',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_SHORTCUT_MAC).map((entry) => createCommand(entry)),
  },
  {
    id: 'windows',
    label: 'Windows',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_SHORTCUT_WIN).map((entry) => createCommand(entry)),
  },
  {
    id: 'system',
    label: '系统',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_SHORTCUT_COMMON).map((entry) => createCommand(entry)),
  },
  {
    id: 'multimedia',
    label: '多媒体',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_MULTIMEDIA).map((entry) => createCommand(entry)),
  },
  {
    id: 'dpi',
    label: 'DPI',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_DPI).map((entry) => createCommand(entry)),
  },
  {
    id: 'profile',
    label: '配置文件',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_PROFILE).map((entry) => createCommand(entry)),
  },
  {
    id: 'disable',
    label: '禁用',
    kind: 'commands',
    commands: Object.entries(KEY_MAP_DISABLE).map((entry) => createCommand(entry)),
  },
  {
    id: 'combination',
    label: '键盘组合键',
    kind: 'panel',
    description: '自定义修饰键 + 键码',
  },
  {
    id: 'macro',
    label: '宏',
    kind: 'panel',
    description: '录制与绑定宏',
  },
];

// JS KeyboardEvent.code → HID Usage（组合键监听 / 宏录制 / 宏按键赋值共用）
const JS_KEY_TO_HID = {
  Escape: 0x29,
  F1: 0x3A, F2: 0x3B, F3: 0x3C, F4: 0x3D, F5: 0x3E, F6: 0x3F,
  F7: 0x40, F8: 0x41, F9: 0x42, F10: 0x43, F11: 0x44, F12: 0x45,
  Backquote: 0x35,
  Digit1: 0x1E, Digit2: 0x1F, Digit3: 0x20, Digit4: 0x21, Digit5: 0x22,
  Digit6: 0x23, Digit7: 0x24, Digit8: 0x25, Digit9: 0x26, Digit0: 0x27,
  Minus: 0x2D, Equal: 0x2E, Backspace: 0x2A,
  Tab: 0x2B,
  KeyQ: 0x14, KeyW: 0x1A, KeyE: 0x08, KeyR: 0x15, KeyT: 0x17,
  KeyY: 0x1C, KeyU: 0x18, KeyI: 0x0C, KeyO: 0x12, KeyP: 0x13,
  BracketLeft: 0x2F, BracketRight: 0x30, Backslash: 0x31,
  CapsLock: 0x39,
  KeyA: 0x04, KeyS: 0x16, KeyD: 0x07, KeyF: 0x09, KeyG: 0x0A,
  KeyH: 0x0B, KeyJ: 0x0D, KeyK: 0x0E, KeyL: 0x0F,
  Semicolon: 0x33, Quote: 0x34, Enter: 0x28,
  KeyZ: 0x1D, KeyX: 0x1B, KeyC: 0x06, KeyV: 0x19, KeyB: 0x05,
  KeyN: 0x11, KeyM: 0x10, Comma: 0x36, Period: 0x37, Slash: 0x38,
  Space: 0x2C,
  PrintScreen: 0x46, ScrollLock: 0x47, Pause: 0x48,
  Insert: 0x49, Home: 0x4A, PageUp: 0x4B,
  Delete: 0x4C, End: 0x4D, PageDown: 0x4E,
  ArrowUp: 0x52, ArrowLeft: 0x50, ArrowDown: 0x51, ArrowRight: 0x4F,
  NumLock: 0x53, NumpadDivide: 0x54, NumpadMultiply: 0x55,
  NumpadSubtract: 0x56, NumpadAdd: 0x57, NumpadEnter: 0x58,
  Numpad1: 0x59, Numpad2: 0x5A, Numpad3: 0x5B, Numpad4: 0x5C,
  Numpad5: 0x5D, Numpad6: 0x5E, Numpad7: 0x5F, Numpad8: 0x60,
  Numpad9: 0x61, Numpad0: 0x62, NumpadDecimal: 0x63,
  // 修饰键（宏可独立记录）
  ShiftLeft: 0xE1, ShiftRight: 0xE5,
  ControlLeft: 0xE0, ControlRight: 0xE4,
  AltLeft: 0xE2, AltRight: 0xE6,
  MetaLeft: 0xE3, MetaRight: 0xE7,
};

const HID_MODIFIER_NAMES = {
  0xE0: 'L Ctrl', 0xE4: 'R Ctrl',
  0xE1: 'L Shift', 0xE5: 'R Shift',
  0xE2: 'L Alt', 0xE6: 'R Alt',
  0xE3: 'L Win', 0xE7: 'R Win',
};

const resolveHidKeyName = (hidCode) => {
  if (hidCode >= 0xE0 && hidCode <= 0xE7) {
    return HID_MODIFIER_NAMES[hidCode] || `Mod 0x${hidCode.toString(16)}`;
  }
  const keyInfo = KEY_CODES.find((k) => k.code === hidCode);
  return keyInfo ? keyInfo.label.replace('\n', '') : `0x${hidCode.toString(16)}`;
};

const DEFAULT_OPEN_CATEGORIES = {
  basic: true,
  edit: true,
  mac: false,
  windows: false,
  system: false,
  multimedia: true,
  dpi: false,
  profile: false,
  disable: false,
  combination: false,
  macro: false,
};

// 按键序号映射表
const BUTTON_INDEX_MAP = {
  'left': 0,       // 左键
  'right': 1,      // 右键
  'middle': 2,     // 中键
  'button4': 3,    // B4 (前侧键)
  'button5': 4,    // B5 (后侧键)
  'dpi': 5,        // DPI
  'reportRate': 6, // reportRate
  'pair': 7,       // Pair
  'nc1': 8,        // NC
  'nc2': 9,        // NC
  'scrollUp': 10,  // 滚轮上
  'scrollDown': 11 // 滚轮下
};

// 按键默认值映射表（按键名 -> 默认功能值）
const BUTTON_DEFAULT_VALUES = {
  'left': '0x0100F000',       // 左键
  'right': '0x0100F100',      // 右键
  'middle': '0x0100F200',     // 中键
  'button4': '0x0100F300',    // 前侧键
  'button5': '0x0100F400',    // 后侧键
  'dpi': '0x07000100',        // DPI+ (默认DPI切换)
  'reportRate': '0x00000000', // 禁用
  'pair': '0x00000000',       // 禁用
  'nc1': '0x00000000',        // 禁用
  'nc2': '0x00000000',        // 禁用
  'scrollUp': '0x0100F801',   // 向上滚动
  'scrollDown': '0x0100F8FF'  // 向下滚动
};

const MOUSE_PREVIEW_MIN_SCALE = 0.45;
const MOUSE_PREVIEW_MAX_SCALE = 1.65;
const MOUSE_PREVIEW_WIDTH_WITH_CONTROLS = 720;
const MOUSE_PREVIEW_HEIGHT_WITH_CONTROLS = 460;

const KeyFunction = ({ macroWorkspace = false }) => {
  const { dataStore, deviceInfo, isConnected, sender, protocolVersion } = useDevice();
  const mouseVisual =
    getMouseVisual(deviceInfo?.modelId) || getMouseVisual('darmoshark-m9');
  // 鼠标移动宏事件自协议 V3.0 起支持；未连接设备时按支持处理，便于编辑本地宏。
  const canUseMouseMoveEvent = supportsMacroMouseMove(protocolVersion);
  // 4.0 起新绑定写宏12，循环落在宏对象；读回时以绑定类型为准（宏12 / 宏9）。
  const macroLoopInMacroObject = macroLoopLivesInMacroObject(protocolVersion);
  // 添加/插入事件菜单的分类列表（已按设备协议过滤）
  const macroEventMenuGroups = useMemo(
    () => visibleMacroEventGroups(canUseMouseMoveEvent),
    [canUseMouseMoveEvent]
  );
  const [activeTab, setActiveTab] = useState('basic');
  // 左侧分配模式：命令 | 按键(组合键) | 宏
  const [assignMode, setAssignMode] = useState('command'); // 'command' | 'key' | 'macro'
  const [isPreviewFirst, setIsPreviewFirst] = useState(
    () =>
      readPanelOrder(KEY_FUNCTION_PANEL_ORDER_KEY, 'assign-first') ===
      'preview-first'
  );
  const previewSideLabel = isPreviewFirst ? '左侧' : '右侧';
  const [commandSearch, setCommandSearch] = useState('');
  const [openCategories, setOpenCategories] = useState(() => ({ ...DEFAULT_OPEN_CATEGORIES }));
  const [informationData, setInformationData] = useState(null);
  const [mouseButtonData, setMouseButtonData] = useState(null);
  const [selectedButton, setSelectedButton] = useState(null); // 当前选中的预览按键
  const [autoJumpToTemplate, setAutoJumpToTemplate] = useState(false); // 自动跳转到相关模版
  const mousePreviewPaneRef = useRef(null);
  const [mousePreviewScale, setMousePreviewScale] = useState(1);

  useEffect(() => {
    const previewPane = mousePreviewPaneRef.current;
    if (!previewPane) return undefined;

    const updatePreviewScale = () => {
      const { width, height } = previewPane.getBoundingClientRect();
      const nextScale = Math.min(
        MOUSE_PREVIEW_MAX_SCALE,
        Math.max(
          MOUSE_PREVIEW_MIN_SCALE,
          Math.min(
            width / MOUSE_PREVIEW_WIDTH_WITH_CONTROLS,
            height / MOUSE_PREVIEW_HEIGHT_WITH_CONTROLS
          )
        )
      );

      setMousePreviewScale((currentScale) =>
        Math.abs(currentScale - nextScale) < 0.01 ? currentScale : nextScale
      );
    };

    updatePreviewScale();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updatePreviewScale);
      return () => window.removeEventListener('resize', updatePreviewScale);
    }

    const resizeObserver = new ResizeObserver(updatePreviewScale);
    resizeObserver.observe(previewPane);
    return () => resizeObserver.disconnect();
  }, []);

  // 组合键状态
  const [selectedModifiers, setSelectedModifiers] = useState([]); // 选中的修饰键
  const [selectedKeyCode, setSelectedKeyCode] = useState(null); // 选中的键码
  const [isListening, setIsListening] = useState(false); // 是否监听键盘

  // 宏状态
  const [macros, setMacros] = useState(loadLocalMacros); // 板载宏 + 网页本地宏
  const [selectedMacro, setSelectedMacro] = useState(null); // 当前选中的宏
  const [draggedLocalMacroId, setDraggedLocalMacroId] = useState(null);
  const [isBoardDropActive, setIsBoardDropActive] = useState(false);
  // 拖到鼠标按键上分配：{ kind: 'command' | 'key' | 'macro', value, label, macroId, onboard }
  const [assignDrag, setAssignDrag] = useState(null);
  const [dropTargetButton, setDropTargetButton] = useState(null); // { key, state: 'valid' | 'invalid' }
  const [importingMacroId, setImportingMacroId] = useState(null);
  const [boardMacrosOpen, setBoardMacrosOpen] = useState(true);
  const [localMacrosOpen, setLocalMacrosOpen] = useState(true);
  const [macroEditorOpen, setMacroEditorOpen] = useState(false); // 宏编辑弹窗
  // 新建流程：name → edit
  const [macroWizardStep, setMacroWizardStep] = useState('edit'); // 'name' | 'edit'
  const [macroDraftName, setMacroDraftName] = useState('');
  const [macroDraftType, setMacroDraftType] = useState('record'); // record | manual
  const [macroIsNew, setMacroIsNew] = useState(false); // 新建未保存前不进列表
  const [macroRecords, setMacroRecords] = useState([]); // 当前宏的按键记录
  const [isMacroRecording, setIsMacroRecording] = useState(false); // 是否正在录制宏
  const [delayMode, setDelayMode] = useState('none'); // 延时模式: none=不记录延迟, dynamic=记录延迟
  // 按键宏绑定的循环设置（编辑中的本地状态）
  const [bindingLoopMode, setBindingLoopMode] = useState('once');
  const [bindingLoopCount, setBindingLoopCount] = useState(1);
  // 宏对象上的循环设置（4.0 宏界面编辑 / 列表「循环」按钮）
  const [macroLoopMode, setMacroLoopMode] = useState('once');
  const [macroLoopCount, setMacroLoopCount] = useState(MACRO_LOOP_COUNT_MIN);
  const [loopManagerMacroId, setLoopManagerMacroId] = useState(null);
  const [managerLoopMode, setManagerLoopMode] = useState('once');
  const [managerLoopCount, setManagerLoopCount] = useState(MACRO_LOOP_COUNT_MIN);
  const [selectedRecords, setSelectedRecords] = useState([]); // 选中的记录（行选择：删除/拖拽）
  const [selectedDelayIds, setSelectedDelayIds] = useState([]); // 延时输入框多选（批量改值）
  const [selectedKeyIds, setSelectedKeyIds] = useState([]); // 按键输入框多选（批量改键）
  const [editingRecordId, setEditingRecordId] = useState(null); // 正在编辑的记录
  const [contextMenu, setContextMenu] = useState(null); // 右键菜单: { recordId, x, y, submenu: null | 'above' | 'below' }
  const [macroListMenu, setMacroListMenu] = useState(null); // 列表三点菜单: { macroId, source, x, y, anchor }
  const [macroListMenuPos, setMacroListMenuPos] = useState(null);
  const macroListMenuRef = useRef(null);
  const [editingMacroName, setEditingMacroName] = useState(null); // 正在编辑名称的宏ID
  const [tempMacroName, setTempMacroName] = useState(''); // 临时宏名称
  const [showAddEventMenu, setShowAddEventMenu] = useState(false); // 显示添加事件类型菜单
  // 添加事件菜单的固定定位结果 { left, width, top, maxHeight }；null 表示还没量高定位
  const [addEventMenuPos, setAddEventMenuPos] = useState(null);
  const [contextMenuPos, setContextMenuPos] = useState(null); // 右键菜单收进视口后的坐标
  const [contextSubmenuPos, setContextSubmenuPos] = useState(null); // 插入子菜单的固定定位结果
  const addEventButtonRef = useRef(null);
  const addEventMenuRef = useRef(null);
  const contextMenuRef = useRef(null);
  const contextSubmenuRef = useRef(null);
  // 数值输入草稿: `${recordId}:${axis}` -> 文本；axis 为 x/y（鼠标移动）或 v（滚轮、鼠标X、鼠标Y）
  const [moveDrafts, setMoveDrafts] = useState({});
  const [showDataOverflowModal, setShowDataOverflowModal] = useState(false); // 显示数据超限弹窗
  // 宏记录：Windows 式多选 / 框选 / 拖拽排序
  const [dragOverIndex, setDragOverIndex] = useState(null); // 插入位置指示
  const [isDraggingRecords, setIsDraggingRecords] = useState(false);
  const [marqueeRect, setMarqueeRect] = useState(null); // 框选矩形（相对列表容器）
  const recordsListRef = useRef(null);
  const lastSelectedIndexRef = useRef(-1); // Shift 连选锚点
  const selectionAnchorRef = useRef(-1);
  const delaySelectAnchorRef = useRef(-1); // 延时输入框 Shift 连选锚点（delay 序列下标）
  const keySelectAnchorRef = useRef(-1); // 按键输入框 Shift 连选锚点（key 序列下标）
  const dragSessionRef = useRef(null); // 拖拽会话
  const marqueeSessionRef = useRef(null); // 框选会话
  const suppressClickRef = useRef(false); // 拖拽/框选后抑制 click
  const selectedRecordsRef = useRef([]);
  const selectedDelayIdsRef = useRef([]);
  const selectedKeyIdsRef = useRef([]);
  const macroRecordsRef = useRef([]);
  useEffect(() => { selectedRecordsRef.current = selectedRecords; }, [selectedRecords]);
  useEffect(() => { selectedDelayIdsRef.current = selectedDelayIds; }, [selectedDelayIds]);
  useEffect(() => { selectedKeyIdsRef.current = selectedKeyIds; }, [selectedKeyIds]);
  useEffect(() => { macroRecordsRef.current = macroRecords; }, [macroRecords]);

  // 本地宏只写入浏览器存储；板载宏始终由设备回读维护。
  useEffect(() => {
    const localMacros = macros
      .filter((macro) => !macro.fromDevice)
      .map(({ macroIndex, rawData, ...macro }) => ({
        ...macro,
        source: 'local',
        fromDevice: false,
      }));
    try {
      localStorage.setItem(LOCAL_MACRO_STORAGE_KEY, JSON.stringify(localMacros));
    } catch (error) {
      console.warn('保存本地宏失败:', error);
    }
  }, [macros]);

  // 解析设备宏数据
  const parseDeviceMacros = (macroData) => {
    if (!macroData?.parsed?.macroObjects) return [];

    const macroObjects = macroData.parsed.macroObjects;
    const parsedMacros = [];

    console.log(`解析 ${macroObjects.length} 个宏对象`);

    macroObjects.forEach((macroObj, index) => {
      // 解析事件为记录
      // 事件格式: [动作|类型][值][延迟低][延迟高]
      // Byte0: bit7=动作(1抬起,0按下), 低7位=类型
      // Byte1: 操作值 (键码/按钮/数值)
      // 类型: 1键盘,2修饰键,3多媒体,4鼠标按键,5滚轮,6鼠标X,7鼠标Y,8鼠标移动(V3.0)
      // 类型8: Byte1-3 为 X/Y 位移，无延时字段，设备固定 5ms
      const records = [];
      let recordIdBase = Date.now() + index * 10000;

      macroObj.events.forEach((event, eventIndex) => {
        let displayName = '';
        const value = event.value;
        let recordType = 'key';

        // 鼠标移动（类型8）：Byte1-3 是 X/Y 位移，固定 5ms 延时，不生成延时记录
        if (event.type === MACRO_EVENT_TYPE_MOUSE_MOVE) {
          const moveX = clampMacroMoveX(event.moveX ?? 0);
          const moveY = clampMacroMoveY(event.moveY ?? 0);
          records.push({
            id: recordIdBase++,
            type: 'move',
            eventType: MACRO_EVENT_TYPE_MOUSE_MOVE,
            typeName: event.typeName,
            moveX,
            moveY,
            value: 0,
            keyName: formatMacroMoveLabel(moveX, moveY),
            action: 'move',
            delay: MACRO_MOUSE_MOVE_DELAY_MS,
            rawEvent: event.raw
          });
          return;
        }

        // 记录里保存的数值口径：类型4 为位掩码，类型5/6/7 为有符号数，其余为原始字节
        let recordValue = value;
        let recordAction = event.action;  // 'up' 或 'down'

        // 根据类型确定显示名称
        switch (event.type) {
          case 1: // 键盘按键
            const keyInfo = KEY_CODES.find(k => k.code === value);
            displayName = keyInfo ? keyInfo.label.replace('\n', '') : `键码 0x${value.toString(16).padStart(2, '0')}`;
            break;
          case 2: // 修饰键
            const modNames = { 0xE0: 'L Ctrl', 0xE1: 'L Shift', 0xE2: 'L Alt', 0xE3: 'L Win',
                               0xE4: 'R Ctrl', 0xE5: 'R Shift', 0xE6: 'R Alt', 0xE7: 'R Win' };
            displayName = modNames[value] || `修饰键 0x${value.toString(16).padStart(2, '0')}`;
            break;
          case 3: // 多媒体键
            displayName = `多媒体 0x${value.toString(16).padStart(2, '0')}`;
            break;
          case MACRO_EVENT_TYPE_MOUSE_BUTTON: // 4 鼠标按键（位掩码）
            recordType = 'mouse';
            recordValue = clampMacroMouseButtons(value);
            displayName = formatMacroMouseButtons(recordValue);
            break;
          case MACRO_EVENT_TYPE_WHEEL:   // 5 滚轮
          case MACRO_EVENT_TYPE_MOUSE_X: // 6 鼠标X
          case MACRO_EVENT_TYPE_MOUSE_Y: // 7 鼠标Y
            recordType = 'axis';
            // 位移类事件只有触发，没有抬起
            recordAction = 'down';
            recordValue = event.axisValue ?? decodeMacroAxisValue(value);
            displayName = formatMacroAxisLabel(event.type, recordValue);
            break;
          default:
            displayName = `${event.typeName} 0x${value.toString(16).padStart(2, '0')}`;
        }

        // 先添加按键记录
        records.push({
          id: recordIdBase++,
          type: recordType,
          eventType: event.type,
          typeName: event.typeName,
          value: recordValue,
          keyCode: recordType === 'mouse' ? recordValue : null,
          valueScheme: recordType === 'mouse' ? 'mask' : undefined,
          keyName: displayName,
          action: recordAction,
          delay: 0,
          rawEvent: event.raw
        });

        // 如果有延时，在按键后面添加延时记录
        if (event.delay > 0) {
          records.push({
            id: recordIdBase++,
            type: 'delay',
            eventType: 0,
            typeName: '延时',
            value: event.delay,
            keyName: `${event.delay}ms`,
            action: 'delay',
            delay: event.delay,
            rawEvent: null
          });
        }
      });

      parsedMacros.push({
        id: `device-${macroObj.index}-${index}`,
        name: macroObj.name,
        macroIndex: macroObj.index,        // 设备中的序号
        deviceDelayMode: macroObj.delayMode, // 延时模式: 0=单独延时, 1+=统一延时(ms)
        objectLength: macroObj.length,     // 对象长度
        records: records,
        // 设备 delayMode: 0=单独延时(动态/记录延迟)，>0=统一延时值
        // UI 仅保留 none / dynamic；统一延时值回落为 dynamic 展示
        delayMode: 'dynamic',
        fixedDelay: macroObj.delayMode > 0 ? macroObj.delayMode : 0,
        // 循环设置：宏12 存在宏对象的 byte5/byte2-3 里，这里原样保留以免重发时被抹平；
        // 宏9 设备这两个字段恒为 1，解析结果等价于「一次」，实际模式看按键绑定。
        loopMode: macroLoopModeFromType(macroObj.type),
        loopCount: clampMacroLoopCount(macroObj.loopCount),
        fromDevice: true,
        source: 'device',
        rawData: macroObj.rawData
      });
    });

    return parsedMacros;
  };

  // 监听设备数据更新
  useEffect(() => {
    if (!dataStore) return;

    // 获取初始数据
    const info = dataStore.getData('informationData');
    const buttons = dataStore.getData('mouseButtonData');
    const macroData = dataStore.getData('macroData');

    if (info) setInformationData(info);
    if (buttons) setMouseButtonData(buttons);

    // 解析并设置宏数据
    if (macroData) {
      const deviceMacros = parseDeviceMacros(macroData);
      setMacros((prev) => [
        ...deviceMacros,
        ...prev.filter((macro) => !macro.fromDevice),
      ]);
      console.log('从设备加载宏数据:', deviceMacros);
    }

    // 监听数据更新
    const handleInformationUpdate = (data) => {
      console.log('按键功能 - 通告信息更新:', data);
      setInformationData(data);
    };

    const handleMouseButtonUpdate = (data) => {
      console.log('按键功能 - 按键数据更新:', data);
      setMouseButtonData(data);
    };

    const handleMacroUpdate = (data) => {
      console.log('按键功能 - 宏数据更新:', data);
      const deviceMacros = parseDeviceMacros(data);
      setMacros((prev) => [
        ...deviceMacros,
        ...prev.filter((macro) => !macro.fromDevice),
      ]);
    };

    dataStore.on('informationData', handleInformationUpdate);
    dataStore.on('mouseButtonData', handleMouseButtonUpdate);
    dataStore.on('macroData', handleMacroUpdate);

    return () => {
      dataStore.off('informationData', handleInformationUpdate);
      dataStore.off('mouseButtonData', handleMouseButtonUpdate);
      dataStore.off('macroData', handleMacroUpdate);
    };
  }, [dataStore]);

  // 获取当前Profile索引
  const getCurrentProfile = () => {
    if (!informationData?.parsed) return 5;
    return (informationData.parsed.profileIndex || 0) + 1;
  };

  // 解析键盘组合键 (00开头的值)
  const parseCombinationKey = (hexValue) => {
    // 格式: 0x00[modifier][keycode]00
    const hex = hexValue.replace('0x', '').toUpperCase();
    if (hex.length !== 8 || !hex.startsWith('00')) return null;

    const modifierByte = parseInt(hex.substring(2, 4), 16);
    const keyCode = parseInt(hex.substring(4, 6), 16);

    if (modifierByte === 0 && keyCode === 0) return null; // 禁用

    const parts = [];

    // 解析修饰键
    if (modifierByte & 0x01) parts.push('Ctrl');
    if (modifierByte & 0x02) parts.push('Shift');
    if (modifierByte & 0x04) parts.push('Alt');
    if (modifierByte & 0x08) parts.push('Win');

    // 查找键码对应的标签
    if (keyCode > 0) {
      const keyInfo = KEY_CODES.find(k => k.code === keyCode);
      if (keyInfo) {
        parts.push(keyInfo.label.replace('\n', ''));
      } else {
        parts.push(`0x${keyCode.toString(16).toUpperCase()}`);
      }
    }

    return parts.length > 0 ? parts.join(' + ') : null;
  };

  // 解析按键功能（根据按键值返回功能描述）
  const getButtonFunction = (buttonValue) => {
    if (!buttonValue) return '未定义';

    // 首先尝试直接匹配（字符串格式，如 "0x01000000"）
    if (KEY_MAP[buttonValue]) {
      return KEY_MAP[buttonValue];
    }

    // 如果是数字格式，转换为完整的32位字符串再匹配
    if (typeof buttonValue === 'number') {
      // 将数字转换为32位大端序格式（如 0x01 -> "0x01000000"）
      const hex32 = '0x' + buttonValue.toString(16).padStart(2, '0').toUpperCase() + '000000';
      if (KEY_MAP[hex32]) {
        return KEY_MAP[hex32];
      }
      // 如果没匹配到，返回未定义
      return '未定义';
    }

    // 检查是否是键盘组合键 (00开头)
    if (typeof buttonValue === 'string' && buttonValue.toUpperCase().startsWith('0X00')) {
      const combinationLabel = parseCombinationKey(buttonValue);
      if (combinationLabel) {
        return combinationLabel;
      }
    }

    // 宏9：0x09[次数][序号][类型]；宏12：0x0C01[序号]00
    const macroBinding = parseMacroBindingValue(buttonValue);
    if (macroBinding) {
      const { macroIndex } = macroBinding;
      const macro = macros.find(
        (item) => item.macroIndex === macroIndex || item.index === macroIndex
      );
      return macro ? macro.name : `宏 ${macroIndex + 1}`;
    }

    // 字符串格式但没匹配到，返回未定义
    return '未定义';
  };

  // 解析选中按键的宏绑定详细信息
  const getSelectedButtonMacroInfo = () => {
    if (!selectedButton || !mouseButtonData?.parsed) return null;

    const parsedKeyMap = {
      'left': 'leftButton',
      'right': 'rightButton',
      'middle': 'middleButton',
      'button4': 'button4',
      'button5': 'button5',
      'dpi': 'dpiButton',
      'reportRate': 'reportRateButton',
      'pair': 'pairButton',
      'scrollUp': 'scrollUp',
      'scrollDown': 'scrollDown'
    };

    const parsedKey = parsedKeyMap[selectedButton];
    if (!parsedKey) return null;

    const buttonValue = mouseButtonData.parsed[parsedKey];
    const binding = parseMacroBindingValue(buttonValue);
    if (!binding) return null;

    const { macroIndex } = binding;
    // 只有 4.0 及以上才认宏12；更低版本即使读到 0x0C 也按宏9 处理。
    const loopInMacroObject = binding.loopInMacroObject && macroLoopInMacroObject;
    const macro = macros.find(m => m.macroIndex === macroIndex);
    const macroName = macro ? macro.name : `宏 ${macroIndex + 1}`;

    // 宏12：循环从宏对象读；宏9：循环从按键绑定读
    if (loopInMacroObject) {
      const loopMode = macro?.loopMode || 'once';
      return {
        macroIndex,
        bindingType: binding.bindingType,
        loopInMacroObject: true,
        macroType: macroLoopTypeFromMode(loopMode),
        macroName,
        loopMode,
        loopCount: clampMacroLoopCount(macro?.loopCount ?? MACRO_LOOP_COUNT_MIN),
        rawValue: buttonValue
      };
    }

    return {
      macroIndex,
      bindingType: binding.bindingType,
      loopInMacroObject: false,
      macroType: binding.loopType,
      macroName,
      loopMode: macroLoopModeFromType(binding.loopType),
      loopCount: binding.loopCount,
      rawValue: buttonValue
    };
  };

  // 宏12：循环模式写在宏对象上，改设置要整表重发宏数据，按键绑定一个字节都不动
  const updateMacroObjectLoop = async (macroIndex, newLoopMode, newLoopCount) => {
    if (!dataStore) return;

    const loopCount = clampMacroLoopCount(newLoopCount);
    const updatedMacros = macros.map((macro) =>
      macro.fromDevice && macro.macroIndex === macroIndex
        ? { ...macro, loopMode: newLoopMode, loopCount }
        : macro
    );

    const onboardMacros = updatedMacros.filter((macro) => macro.fromDevice);
    const macroObjects = onboardMacros.map((macro, index) =>
      buildMacroObject(macro, macro.macroIndex !== undefined ? macro.macroIndex : index)
    );
    const macroData = dataStore.buildMacroData(macroObjects);

    console.log(
      `更新宏循环设置(宏12): 宏序号=${macroIndex}, 模式=${newLoopMode}, 次数=${loopCount}, 宏数据 ${macroData.length} 字节`
    );

    await sender.setMacroData(macroData);
    setMacros(updatedMacros);
    await sender.wait(200);
    await sender.getMacroData(1);
  };

  // 宏9：循环模式编码在按键绑定键值里
  const updateBindingLoop = async (buttonIndex, macroIndex, newLoopMode, newLoopCount) => {
    const keyValue = encodeMacroBindingValue({
      bindingType: MACRO_BINDING_TYPE_LEGACY,
      macroIndex,
      loopMode: newLoopMode,
      loopCount: newLoopCount,
    });

    console.log(`更新按键宏循环设置: ${selectedButton} -> ${keyValue}, 模式: ${newLoopMode}, 次数: ${newLoopCount}`);
    await sender.setButtonMapping(buttonIndex, keyValue);
    await sender.wait(100);
    await sender.getMouseButtonData();
  };

  // 更新选中按键的宏循环设置
  const updateSelectedButtonMacroLoop = async (newLoopMode, newLoopCount) => {
    if (!selectedButton || !isConnected || !sender) return;

    const macroInfo = getSelectedButtonMacroInfo();
    if (!macroInfo) return;

    const buttonIndex = BUTTON_INDEX_MAP[selectedButton];
    if (buttonIndex === undefined) return;

    try {
      // 4.0 及以上只走宏12：循环写进宏数据；残留的宏9 一并重绑成宏12。
      // 4.0 以下只走宏9：循环继续写在按键绑定里。
      if (macroLoopInMacroObject) {
        await updateMacroObjectLoop(macroInfo.macroIndex, newLoopMode, newLoopCount);
        if (!macroInfo.loopInMacroObject) {
          const keyValue = encodeNewMacroBinding(protocolVersion, macroInfo.macroIndex);
          console.log(`4.0 重绑宏9 -> 宏12: ${selectedButton} -> ${keyValue}`);
          await sender.setButtonMapping(buttonIndex, keyValue);
          await sender.wait(100);
          await sender.getMouseButtonData();
        }
      } else {
        await updateBindingLoop(buttonIndex, macroInfo.macroIndex, newLoopMode, newLoopCount);
      }
      toast.success('循环设置已更新');
    } catch (error) {
      console.error('更新循环设置失败:', error);
      toast.error('更新失败: ' + error.message);
    }
  };

  const openLoopManager = (macroId) => {
    const macro = macros.find((item) => item.id === macroId);
    if (!macro) return;
    setLoopManagerMacroId(macroId);
    setManagerLoopMode(macro.loopMode || 'once');
    setManagerLoopCount(clampMacroLoopCount(macro.loopCount));
  };

  const closeLoopManager = () => setLoopManagerMacroId(null);

  const applyLoopManager = async () => {
    const macro = macros.find((item) => item.id === loopManagerMacroId);
    if (!macro) return;

    const loopCount = clampMacroLoopCount(managerLoopCount);

    if (macro.fromDevice) {
      if (!isConnected || !sender || !dataStore) {
        toast.error('设备未连接');
        return;
      }
      try {
        await updateMacroObjectLoop(macro.macroIndex, managerLoopMode, loopCount);
        toast.success('循环设置已更新');
      } catch (error) {
        console.error('更新宏循环失败:', error);
        toast.error('更新失败: ' + error.message);
        return;
      }
    } else {
      setMacros((prev) =>
        prev.map((item) =>
          item.id === macro.id
            ? { ...item, loopMode: managerLoopMode, loopCount }
            : item
        )
      );
      toast.success('循环设置已更新');
    }

    if (selectedMacro === macro.id) {
      setMacroLoopMode(managerLoopMode);
      setMacroLoopCount(loopCount);
    }
    closeLoopManager();
  };

  const renderLoopModeOptions = ({
    mode,
    count,
    onModeChange,
    onCountChange,
    countClassName = 'macro-loop-count-input',
    burstCountClassName = 'macro-loop-burst-count-input',
  }) => (
    <div className="macro-loop-options">
      <label className="macro-loop-option">
        <input
          type="radio"
          checked={mode === 'once'}
          onChange={() => onModeChange('once')}
        />
        <span className="macro-loop-option-text">只发一次，停止</span>
      </label>
      <label className="macro-loop-option">
        <input
          type="radio"
          checked={mode === 'release'}
          onChange={() => onModeChange('release')}
        />
        <span className="macro-loop-option-text">按住按键一直发，放开停止</span>
      </label>
      <label className="macro-loop-option">
        <input
          type="radio"
          checked={mode === 'anyKey'}
          onChange={() => onModeChange('anyKey')}
        />
        <span className="macro-loop-option-text">循环发送，直到任意按键按下</span>
      </label>
      <label className="macro-loop-option">
        <input
          type="radio"
          checked={mode === 'toggle'}
          onChange={() => onModeChange('toggle')}
        />
        <span className="macro-loop-option-text">循环发送，直到按键再次按下</span>
      </label>
      <label className="macro-loop-option has-count">
        <input
          type="radio"
          checked={mode === 'count'}
          onChange={() => onModeChange('count')}
        />
        <span className="macro-loop-option-text">
          <span className="macro-loop-option-lead">
            连发
            <input
              type="number"
              className={countClassName}
              value={count}
              onChange={(e) => onCountChange(clampMacroLoopCount(e.target.value))}
              onClick={(e) => {
                e.stopPropagation();
                onModeChange('count');
              }}
              min={MACRO_LOOP_COUNT_MIN}
              max={MACRO_LOOP_COUNT_MAX}
            />
          </span>
          <span className="macro-loop-option-tail">次，到发送完成后停止</span>
        </span>
      </label>
      {macroLoopInMacroObject && (
        <label className="macro-loop-option has-count">
          <input
            type="radio"
            checked={mode === 'burstAnyKey'}
            onChange={() => onModeChange('burstAnyKey')}
          />
          <span className="macro-loop-option-text">
            <span className="macro-loop-option-lead">
              连发
              <input
                type="number"
                className={burstCountClassName}
                value={count}
                onChange={(e) => onCountChange(clampMacroLoopCount(e.target.value))}
                onClick={(e) => {
                  e.stopPropagation();
                  onModeChange('burstAnyKey');
                }}
                min={MACRO_LOOP_COUNT_MIN}
                max={MACRO_LOOP_COUNT_MAX}
              />
            </span>
            <span className="macro-loop-option-tail">次，直到任意按键按下</span>
          </span>
        </label>
      )}
    </div>
  );

  // 当选中按键变化或按键数据更新时，同步本地循环设置状态
  // 宏12 的循环设置来自宏对象，宏列表刷新后也要重新同步
  useEffect(() => {
    const macroInfo = getSelectedButtonMacroInfo();
    if (macroInfo) {
      setBindingLoopMode(macroInfo.loopMode);
      setBindingLoopCount(macroInfo.loopCount);
    }
  }, [selectedButton, mouseButtonData, macros]);

  const toggleCategory = (id) => {
    setOpenCategories((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const openCategory = (id) => {
    setOpenCategories((prev) => ({ ...prev, [id]: true }));
    setActiveTab(id);
    if (id === 'combination') setAssignMode('key');
    else if (id === 'macro') setAssignMode('macro');
    else setAssignMode('command');
  };

  const matchesSearch = (text, q) => {
    if (!q) return true;
    return String(text || '').toLowerCase().includes(q);
  };

  // 获取实际按键配置（如果有设备数据则使用设备数据，否则使用默认值）
  const getButtonConfig = () => {
    if (!mouseButtonData?.parsed) {
      // 默认按键配置
      return [
        { id: 'left', label: '左键', value: 0x01 },
        { id: 'right', label: '右键', value: 0x02 },
        { id: 'middle', label: '中键', value: 0x04 },
        { id: 'button4', label: '前键', value: 0x08 },
        { id: 'button5', label: '后键', value: 0x10 },
        { id: 'dpi', label: 'DPI', value: 0x20 },
        { id: 'scrollUp', label: '向上滚动', value: null },
        { id: 'scrollDown', label: '向下滚动', value: null },
      ];
    }

    const parsed = mouseButtonData.parsed;
    return [
      { id: 'left', label: '左键', value: parsed.leftButton, function: getButtonFunction(parsed.leftButton) },
      { id: 'right', label: '右键', value: parsed.rightButton, function: getButtonFunction(parsed.rightButton) },
      { id: 'middle', label: '中键', value: parsed.middleButton, function: getButtonFunction(parsed.middleButton) },
      { id: 'button4', label: '前键', value: parsed.button4, function: getButtonFunction(parsed.button4) },
      { id: 'button5', label: '后键', value: parsed.button5, function: getButtonFunction(parsed.button5) },
      { id: 'dpi', label: 'DPI按键', value: parsed.dpiButton, function: getButtonFunction(parsed.dpiButton) },
      { id: 'scrollUp', label: '向上滚动', value: parsed.scrollUp, function: getButtonFunction(parsed.scrollUp) },
      { id: 'scrollDown', label: '向下滚动', value: parsed.scrollDown, function: getButtonFunction(parsed.scrollDown) },
    ];
  };

  const basicButtons = getButtonConfig();

  // 获取鼠标图形上显示的按键标签（只显示实际存在的7个按键，包含功能名称和值）
  const getMouseDisplayLabels = () => {
    if (!mouseButtonData?.parsed) {
      // 默认标签（未连接设备时）
      return {
        left: { label: '左键', value: null },
        right: { label: '右键', value: null },
        middle: { label: '中键', value: null },
        scrollUp: { label: '向上滚动', value: null },
        scrollDown: { label: '向下滚动', value: null },
        button4: { label: '侧键4', value: null },
        button5: { label: '侧键5', value: null }
      };
    }

    const parsed = mouseButtonData.parsed;
    return {
      left: { 
        label: getButtonFunction(parsed.leftButton) || '左键', 
        value: parsed.leftButton 
      },
      right: { 
        label: getButtonFunction(parsed.rightButton) || '右键', 
        value: parsed.rightButton 
      },
      middle: { 
        label: getButtonFunction(parsed.middleButton) || '中键', 
        value: parsed.middleButton 
      },
      scrollUp: { 
        label: getButtonFunction(parsed.scrollUp) || '向上滚动', 
        value: parsed.scrollUp 
      },
      scrollDown: { 
        label: getButtonFunction(parsed.scrollDown) || '向下滚动', 
        value: parsed.scrollDown 
      },
      button4: { 
        label: getButtonFunction(parsed.button4) || '侧键4', 
        value: parsed.button4 
      },
      button5: { 
        label: getButtonFunction(parsed.button5) || '侧键5', 
        value: parsed.button5 
      }
    };
  };

  const mouseLabels = getMouseDisplayLabels();

  // 同步组合键状态 - 仅在选中按键变化时同步一次
  const lastSyncedButton = useRef(null);
  const recordButtonRef = useRef(null); // 录制按钮的 ref
  const submitButtonRef = useRef(null); // 提交按钮的 ref

  useEffect(() => {
    // 只有当选中的按键变化时才同步
    if (selectedButton === lastSyncedButton.current) {
      return;
    }

    if (activeTab !== 'combination' || !selectedButton) {
      lastSyncedButton.current = selectedButton;
      return;
    }

    lastSyncedButton.current = selectedButton;

    const currentValue = mouseLabels[selectedButton]?.value;
    if (!currentValue || typeof currentValue !== 'string') {
      setSelectedModifiers([]);
      setSelectedKeyCode(null);
      return;
    }

    // 检查是否是组合键格式 (0x00开头)
    if (currentValue.toUpperCase().startsWith('0X00')) {
      const hex = currentValue.replace('0x', '').replace('0X', '').toUpperCase();
      if (hex.length === 8) {
        const modifierByte = parseInt(hex.substring(2, 4), 16);
        const keyCode = parseInt(hex.substring(4, 6), 16);

        // 解析修饰键
        const mods = [];
        if (modifierByte & 0x01) mods.push('ctrl');
        if (modifierByte & 0x02) mods.push('shift');
        if (modifierByte & 0x04) mods.push('alt');
        if (modifierByte & 0x08) mods.push('win');

        setSelectedModifiers(mods);
        setSelectedKeyCode(keyCode > 0 ? keyCode : null);
        return;
      }
    }

    // 不是组合键格式，清空选择
    setSelectedModifiers([]);
    setSelectedKeyCode(null);
  }, [activeTab, selectedButton]);

  // 键盘监听 - 在组合键标签页且开启监听时
  useEffect(() => {
    if (activeTab !== 'combination' || !isListening) return;

    const handleKeyDown = (e) => {
      // 阻止默认行为
      e.preventDefault();

      // 更新修饰键
      const mods = [];
      if (e.ctrlKey) mods.push('ctrl');
      if (e.shiftKey) mods.push('shift');
      if (e.altKey) mods.push('alt');
      if (e.metaKey) mods.push('win');
      setSelectedModifiers(mods);

      // 如果不是纯修饰键，更新键码
      const modifierKeys = ['Control', 'Shift', 'Alt', 'Meta'];
      if (!modifierKeys.includes(e.key)) {
        const hidCode = JS_KEY_TO_HID[e.code];
        if (hidCode) {
          setSelectedKeyCode(hidCode);
          // 更新 lastSyncedButton 以防止被重置
          lastSyncedButton.current = selectedButton;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeTab, selectedButton, isListening]);

  // 清除组合键选择
  const handleClearCombination = () => {
    setSelectedModifiers([]);
    setSelectedKeyCode(null);
    lastSyncedButton.current = selectedButton; // 防止被重置
  };

  // 切换监听状态
  const toggleListening = () => {
    setIsListening(prev => !prev);
  };

  // 处理预览按键点击 - 选择目标按键
  const handlePreviewButtonClick = (buttonId) => {
    if (selectedButton === buttonId) {
      setSelectedButton(null); // 再次点击取消选择
      return;
    }

    setSelectedButton(buttonId);
    console.log('选中预览按键:', buttonId);

    // 如果关闭了自动跳转，则不跳转到对应模版
    if (!autoJumpToTemplate) return;

    // 获取当前按键的绑定值
    const buttonValue = mouseLabels[buttonId]?.value;
    if (!buttonValue || typeof buttonValue !== 'string') return;

    const hex = buttonValue.replace('0x', '').replace('0X', '').toUpperCase();
    if (hex.length !== 8) return;

    const prefix = hex.substring(0, 2);

    // 根据前缀/映射表定位分类并展开
    const upper = String(buttonValue).toUpperCase();
    if (upper === '0X00000000') {
      openCategory('disable');
    } else if (KEY_MAP_BASIC[buttonValue] || KEY_MAP_BASIC[upper]) {
      openCategory('basic');
    } else if (KEY_MAP_SHORTCUT_MAC[buttonValue] || KEY_MAP_SHORTCUT_MAC[upper]) {
      openCategory('mac');
    } else if (KEY_MAP_SHORTCUT_WIN[buttonValue] || KEY_MAP_SHORTCUT_WIN[upper]) {
      openCategory('windows');
    } else if (KEY_MAP_SHORTCUT_COMMON[buttonValue] || KEY_MAP_SHORTCUT_COMMON[upper]) {
      openCategory('system');
    } else if (KEY_MAP_MULTIMEDIA[buttonValue] || KEY_MAP_MULTIMEDIA[upper]) {
      openCategory('multimedia');
    } else if (KEY_MAP_DPI[buttonValue] || KEY_MAP_DPI[upper]) {
      openCategory('dpi');
    } else if (KEY_MAP_PROFILE[buttonValue] || KEY_MAP_PROFILE[upper]) {
      openCategory('profile');
    } else if (prefix === '00') {
      openCategory('combination');
    } else if (prefix === '09' || prefix === '0C') {
      openCategory('macro');
      const macroIndex = parseInt(hex.substring(4, 6), 16);
      const macro = macros.find((m) => m.macroIndex === macroIndex || m.index === macroIndex);
      if (macro) setSelectedMacro(macro.id);
    } else if (prefix === 'FF') {
      openCategory('disable');
    }
  };

  // 把一个功能值写到指定鼠标按键（点击分配与拖放分配共用）
  const applyButtonMapping = async (buttonId, keyValue, options = {}) => {
    const { clearSelection = true, successMessage } = options;

    if (!buttonId) {
      toast.warning('请先在鼠标预览图上选择要修改的按键');
      return false;
    }

    // 检查设备是否连接
    if (!isConnected || !sender) {
      toast.error('设备未连接');
      return false;
    }

    // 获取按键序号
    const buttonIndex = BUTTON_INDEX_MAP[buttonId];
    if (buttonIndex === undefined) {
      toast.error('无效的按键选择');
      return false;
    }

    try {
      console.log(`发送按键映射: ${buttonId}(序号${buttonIndex}) -> ${keyValue}`);
      await sender.setButtonMapping(buttonIndex, keyValue);

      // 发送成功后刷新按键数据
      await sender.wait(100);
      await sender.getMouseButtonData();

      toast.success(successMessage || '按键映射设置成功');

      // 清除选择
      if (clearSelection) setSelectedButton(null);
      return true;
    } catch (error) {
      console.error('设置按键映射失败:', error);
      toast.error('设置失败: ' + error.message);
      return false;
    }
  };

  // 处理模板按键点击 - 发送数据到设备
  const handleKeyMapButtonClick = async (keyValue) =>
    applyButtonMapping(selectedButton, keyValue);

  // ---- 拖放分配：命令 / 按键 / 宏 -> 鼠标按键 ----

  // 开始拖动左侧条目（命令、按键、宏）
  const beginAssignDrag = (event, payload, transferText) => {
    event.dataTransfer.effectAllowed = 'copy';
    try {
      event.dataTransfer.setData('text/plain', transferText ?? payload.label ?? '');
    } catch (error) {
      // Safari 等实现对 setData 更严格，拖拽状态仍由 assignDrag 兜底
    }
    setAssignDrag(payload);
  };

  // 拖动结束（无论是否落在有效目标上）都要清干净高亮
  const endAssignDrag = () => {
    setAssignDrag(null);
    setDropTargetButton(null);
    setDraggedLocalMacroId(null);
    setIsBoardDropActive(false);
  };

  // 判断某个鼠标按键能否接收当前拖拽内容
  const assignDropStateFor = (buttonKey) => {
    if (!assignDrag) return null;
    if (BUTTON_INDEX_MAP[buttonKey] === undefined) return 'invalid';
    if (!isConnected || !sender) return 'invalid';
    // 本地宏没写进设备，绑定按键时设备找不到对应序号
    if (assignDrag.kind === 'macro' && !assignDrag.onboard) return 'invalid';
    return 'valid';
  };

  const handleButtonDragOver = (event, buttonKey) => {
    if (!assignDrag) return;
    event.preventDefault();
    event.stopPropagation();
    const state = assignDropStateFor(buttonKey);
    event.dataTransfer.dropEffect = state === 'valid' ? 'copy' : 'none';
    setDropTargetButton((current) =>
      current && current.key === buttonKey && current.state === state
        ? current
        : { key: buttonKey, state }
    );
  };

  const handleButtonDragLeave = (buttonKey) => {
    setDropTargetButton((current) =>
      current && current.key === buttonKey ? null : current
    );
  };

  const handleButtonDrop = async (event, buttonKey) => {
    if (!assignDrag) return;
    event.preventDefault();
    event.stopPropagation();

    const drag = assignDrag;
    endAssignDrag();

    const buttonName = MOUSE_BUTTON_LABELS[buttonKey] || buttonKey;
    if (BUTTON_INDEX_MAP[buttonKey] === undefined) {
      toast.warning(`${buttonName}不支持自定义功能`);
      return;
    }

    if (drag.kind === 'macro') {
      if (!drag.onboard) {
        toast.warning('本地宏请先拖到「板载宏」导入设备，再绑定到按键');
        return;
      }
      setSelectedButton(buttonKey);
      await bindMacroToButton(drag.macroId, buttonKey);
      return;
    }

    setSelectedButton(buttonKey);
    await applyButtonMapping(buttonKey, drag.value, {
      clearSelection: false,
      successMessage: `已将「${drag.label}」分配到${buttonName}`,
    });
  };

  // 传给热区 / 标签卡片的拖放属性
  const buttonDropHandlers = (buttonKey) => ({
    onDragOver: (event) => handleButtonDragOver(event, buttonKey),
    onDragEnter: (event) => handleButtonDragOver(event, buttonKey),
    onDragLeave: (event) => {
      const next = event.relatedTarget;
      if (next && event.currentTarget.contains && event.currentTarget.contains(next)) return;
      handleButtonDragLeave(buttonKey);
    },
    onDrop: (event) => handleButtonDrop(event, buttonKey),
  });

  // 拖拽高亮用的 class 后缀
  const buttonDropClass = (buttonKey) => {
    if (!dropTargetButton || dropTargetButton.key !== buttonKey) return '';
    return dropTargetButton.state === 'valid' ? 'is-drop-target' : 'is-drop-invalid';
  };

  // 根据标签页获取对应的按键映射表
  const getKeyMapByTab = (tab) => {
    switch (tab) {
      case 'basic': return KEY_MAP_BASIC;
      case 'dpi': return KEY_MAP_DPI;
      case 'profile': return KEY_MAP_PROFILE;
      case 'disable': return KEY_MAP_DISABLE;
      default: return {};
    }
  };

  // 获取当前选中按键的值
  const getSelectedButtonValue = () => {
    if (!selectedButton || !mouseLabels[selectedButton]) return null;
    return mouseLabels[selectedButton].value;
  };

  // 渲染按键网格
  const renderKeyGrid = (keyMap) => {
    const currentValue = getSelectedButtonValue();
    const buttons = Object.entries(keyMap).map(([value, label]) => ({
      id: value,
      label: label,
      value: value
    }));

    return (
      <div className="button-grid">
        {buttons.map(button => {
          const isCurrentMapping = currentValue && currentValue === button.value;
          return (
            <div key={button.id} className="key-button-wrapper">
              <button
                className={`key-button ${isCurrentMapping ? 'current-mapping' : ''}`}
                onClick={() => handleKeyMapButtonClick(button.value)}
              >
                <div className="button-label">{button.label}</div>
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // 渲染快捷键分组
  const renderShortcutGrid = () => {
    const currentValue = getSelectedButtonValue();

    const renderSection = (title, keyMap) => {
      const buttons = Object.entries(keyMap).map(([value, label]) => ({
        id: value,
        label: label,
        value: value
      }));

      return (
        <div className="shortcut-section">
          <div className="section-title">{title}</div>
          <div className="button-grid">
            {buttons.map(button => {
              const isCurrentMapping = currentValue && currentValue === button.value;
              return (
                <div key={button.id} className="key-button-wrapper">
                  <button
                    className={`key-button ${isCurrentMapping ? 'current-mapping' : ''}`}
                    onClick={() => handleKeyMapButtonClick(button.value)}
                  >
                    <div className="button-label">{button.label}</div>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      );
    };

    return (
      <div className="shortcut-sections">
        {renderSection('通用', KEY_MAP_SHORTCUT_COMMON)}
        {renderSection('Mac', KEY_MAP_SHORTCUT_MAC)}
        {renderSection('Windows', KEY_MAP_SHORTCUT_WIN)}
      </div>
    );
  };

  // 切换修饰键选择
  const toggleModifier = (modId) => {
    setSelectedModifiers(prev =>
      prev.includes(modId)
        ? prev.filter(id => id !== modId)
        : [...prev, modId]
    );
  };

  // 计算组合键值
  const getCombinationValue = () => {
    if (!selectedKeyCode) return null;

    // 计算修饰键字节
    let modifierByte = 0;
    selectedModifiers.forEach(modId => {
      const mod = MODIFIER_KEYS.find(m => m.id === modId);
      if (mod) modifierByte |= mod.bit;
    });

    // 格式: 00 [modifier] [keycode] 00
    const hex = '0x00' +
      modifierByte.toString(16).padStart(2, '0').toUpperCase() +
      selectedKeyCode.toString(16).padStart(2, '0').toUpperCase() +
      '00';
    return hex;
  };

  // 获取组合键预览文本
  const getCombinationPreview = () => {
    const parts = [];
    selectedModifiers.forEach(modId => {
      const mod = MODIFIER_KEYS.find(m => m.id === modId);
      if (mod) parts.push(mod.label);
    });
    if (selectedKeyCode) {
      const key = KEY_CODES.find(k => k.code === selectedKeyCode);
      if (key) parts.push(key.label.replace('\n', ''));
    }
    return parts.length > 0 ? parts.join(' + ') : '请选择按键';
  };

  // 保存组合键
  const handleSaveCombination = async () => {
    if (!selectedButton) {
      toast.warning('请先在鼠标预览图上选择要修改的按键');
      return;
    }

    const value = getCombinationValue();
    if (!value) {
      toast.warning('请选择一个键码');
      return;
    }

    await handleKeyMapButtonClick(value);

    // 清除组合键选择
    setSelectedModifiers([]);
    setSelectedKeyCode(null);
  };

  // 单个键码的绑定值：0x00[mod][key]00（无修饰时 mod=00）
  const buildKeyCodeValue = (keyCode) =>
    '0x0000' + keyCode.toString(16).padStart(2, '0').toUpperCase() + '00';

  // 点击按键：直接绑定为 0x00[mod][key]00（无修饰时 mod=00）
  const handleKeyCodePick = async (keyCode) => {
    if (!selectedButton) {
      toast.warning(`请先在${previewSideLabel}选择鼠标按键`);
      return;
    }
    setSelectedKeyCode(keyCode);
    await handleKeyMapButtonClick(buildKeyCodeValue(keyCode));
  };

  // 按键模式：全部按键垂直列表（英文标签，无中文）+ 搜索过滤
  const renderCombinationGrid = () => {
    const currentValue = getSelectedButtonValue();
    let currentKeyCode = null;
    if (currentValue) {
      const hex = String(currentValue).replace(/^0x/i, '').toUpperCase();
      // 格式 0x00[mod][key]00
      if (hex.length === 8 && hex.startsWith('00')) {
        const code = parseInt(hex.substring(4, 6), 16);
        if (code > 0) currentKeyCode = code;
      }
    }

    const q = commandSearch.trim().toLowerCase();
    const filteredKeys = KEY_CODES.filter((key) => {
      if (!q) return true;
      const hexCode = key.code.toString(16).toUpperCase().padStart(2, '0');
      return (
        matchesSearch(key.label, q) ||
        matchesSearch(hexCode, q) ||
        matchesSearch(`0x${hexCode}`, q)
      );
    });

    if (filteredKeys.length === 0) {
      return <div className="cmd-empty">无匹配按键</div>;
    }

    return (
      <div className="key-list-panel">
        <div className="key-list" role="list">
          {filteredKeys.map((key) => {
            const isCurrent = currentKeyCode === key.code;
            const isActive = selectedKeyCode === key.code;
            const keyLabel = key.label.replace('\n', ' ');
            return (
              <button
                key={key.code}
                type="button"
                role="listitem"
                className={`key-list-item assign-draggable ${isCurrent ? 'current' : ''} ${
                  isActive && !isCurrent ? 'active' : ''
                } ${
                  assignDrag?.kind === 'key' && assignDrag.keyCode === key.code ? 'is-dragging' : ''
                }`}
                draggable
                onDragStart={(event) =>
                  beginAssignDrag(event, {
                    kind: 'key',
                    keyCode: key.code,
                    value: buildKeyCodeValue(key.code),
                    label: keyLabel,
                  })
                }
                onDragEnd={endAssignDrag}
                onClick={() => handleKeyCodePick(key.code)}
                title={`0x${key.code.toString(16).toUpperCase().padStart(2, '0')} · 可拖到鼠标按键上分配`}
              >
                <span className="key-list-label">{key.label}</span>
                {isCurrent && <span className="key-list-badge">•</span>}
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  // 宏相关函数
  const normalizeDelayRecords = (records = []) => {
    const normalized = [];

    records.forEach((record) => {
      if (record.type !== 'delay') {
        normalized.push(record);
        return;
      }

      const previous = normalized[normalized.length - 1];
      // 开头的延迟没有可关联事件，设备也不会发送，直接忽略。
      if (!previous) return;

      // 鼠标移动事件没有延时字段（设备固定 5ms），其后的延迟无法下发。
      if (previous.type === 'move') return;

      // 同一事件后的连续延迟合并为一条，避免归属不清。
      if (previous.type === 'delay') {
        const mergedDelay = Math.min(
          65535,
          (previous.delay || previous.value || 0) + (record.delay || record.value || 0)
        );
        normalized[normalized.length - 1] = {
          ...previous,
          delay: mergedDelay,
          value: mergedDelay,
          keyName: `${mergedDelay}ms`,
        };
        return;
      }

      normalized.push(record);
    });

    return normalized;
  };

  const includeOwnedDelays = (records, recordIds) => {
    const ids = new Set(recordIds);
    records.forEach((record, index) => {
      if (!ids.has(record.id) || record.type === 'delay') return;
      const ownedDelay = records[index + 1];
      if (ownedDelay?.type === 'delay') ids.add(ownedDelay.id);
    });
    return ids;
  };

  const getDelayOwnerLabel = (delayIndex) => {
    const owner = macroRecords[delayIndex - 1];
    if (!owner || owner.type === 'delay') return '未关联事件';

    const keyName = owner.keyName || '未设置键值';
    const actionName =
      owner.type === 'move'
        ? '移动'
        : owner.type === 'axis'
          ? '触发'
          : owner.action === 'up'
            ? '抬起'
            : '按下';
    return `${keyName} · ${actionName}`;
  };

  // 鼠标移动记录（协议 V3.0 类型8）：固定 5ms 延时，X/Y 直接写入事件字节
  const createMoveRecord = (id, moveX = 0, moveY = 0) => {
    const x = clampMacroMoveX(moveX);
    const y = clampMacroMoveY(moveY);
    return {
      id,
      type: 'move',
      eventType: MACRO_EVENT_TYPE_MOUSE_MOVE,
      keyCode: null,
      keyName: formatMacroMoveLabel(x, y),
      moveX: x,
      moveY: y,
      value: 0,
      modifiers: [],
      delay: MACRO_MOUSE_MOVE_DELAY_MS,
      action: 'move',
    };
  };

  // 滚轮 / 鼠标X / 鼠标Y（类型 5/6/7）：Byte1 是 -127~127 的有符号值，
  // 只有触发没有抬起，延时字段仍然可用。
  const createAxisRecord = (id, eventType, value = 0) => {
    const amount = clampMacroAxisValue(value);
    return {
      id,
      type: 'axis',
      eventType,
      keyCode: null,
      keyName: formatMacroAxisLabel(eventType, amount),
      value: amount,
      modifiers: [],
      delay: 0,
      action: 'down',
    };
  };

  // 记录里的鼠标按键位掩码（历史数据只写了 keyCode）
  const getMouseButtonMask = (record) =>
    clampMacroMouseButtons(record.value ?? record.keyCode);

  // 鼠标按键记录（类型4）：Byte1 是按键位掩码，不是按键序号
  const createMouseButtonRecord = (id, mask = MACRO_MOUSE_BUTTON_LEFT, action = 'down') => {
    const bits = clampMacroMouseButtons(mask);
    return {
      id,
      type: 'mouse',
      eventType: MACRO_EVENT_TYPE_MOUSE_BUTTON,
      valueScheme: 'mask',
      keyCode: bits,
      keyName: formatMacroMouseButtons(bits),
      value: bits,
      modifiers: [],
      delay: 0,
      action,
    };
  };

  // 修改鼠标移动记录的 X / Y 位移
  const updateMoveValue = (recordId, axis, rawValue) => {
    const parsed = parseInt(rawValue, 10);
    const value = Number.isFinite(parsed) ? parsed : 0;

    setMacroRecords((prev) =>
      prev.map((r) => {
        if (r.id !== recordId) return r;

        // axis='v'：滚轮 / 鼠标X / 鼠标Y 的单值输入
        if (axis === 'v') {
          if (r.type !== 'axis') return r;
          const amount = clampMacroAxisValue(value);
          return { ...r, value: amount, keyName: formatMacroAxisLabel(r.eventType, amount) };
        }

        if (r.type !== 'move') return r;
        const moveX = axis === 'x' ? clampMacroMoveX(value) : clampMacroMoveX(r.moveX ?? 0);
        const moveY = axis === 'y' ? clampMacroMoveY(value) : clampMacroMoveY(r.moveY ?? 0);
        return { ...r, moveX, moveY, keyName: formatMacroMoveLabel(moveX, moveY) };
      })
    );
  };

  // 输入草稿：输入过程中保留原文本，"-"、"" 这类中间态不写回数值，
  // 否则刚敲下负号就会被解析成 0 而无法输入负值。
  const moveDraftKey = (recordId, axis) => `${recordId}:${axis}`;

  const getMoveFieldValue = (record, axis) => {
    const draft = moveDrafts[moveDraftKey(record.id, axis)];
    if (draft !== undefined) return draft;
    if (axis === 'v') return String(record.value ?? 0);
    return String((axis === 'x' ? record.moveX : record.moveY) ?? 0);
  };

  const handleMoveInputChange = (recordId, axis, rawValue) => {
    // 只接受可选负号 + 数字
    if (!/^-?\d*$/.test(rawValue)) return;

    setMoveDrafts((prev) => ({ ...prev, [moveDraftKey(recordId, axis)]: rawValue }));

    const parsed = parseInt(rawValue, 10);
    if (!Number.isFinite(parsed)) return;  // "" / "-"：等失焦再定值
    updateMoveValue(recordId, axis, parsed);
  };

  // 失焦时按协议范围规范化显示（超出上下限的输入在此收敛）
  const commitMoveInput = (recordId, axis) => {
    const key = moveDraftKey(recordId, axis);
    const draft = moveDrafts[key];
    if (draft !== undefined) {
      const parsed = parseInt(draft, 10);
      updateMoveValue(recordId, axis, Number.isFinite(parsed) ? parsed : 0);
    }
    setMoveDrafts((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // 获取下一个可用的宏序号（最大序号+1，如果冲突则继续+1）
  const getNextMacroIndex = () => {
    const onboardMacros = macros.filter((macro) => macro.fromDevice);
    if (onboardMacros.length === 0) return 0;

    // 获取所有已使用的序号
    const usedIndexes = new Set(onboardMacros.map(m => m.macroIndex ?? 0));

    // 找到最大序号
    const maxIndex = Math.max(...onboardMacros.map(m => m.macroIndex ?? 0));

    // 从最大序号+1开始，找一个不冲突的序号
    let nextIndex = maxIndex + 1;
    while (usedIndexes.has(nextIndex)) {
      nextIndex++;
    }

    return nextIndex;
  };

  const addNewMacro = () => {
    // 新建先不入列表，向导完成并保存后才出现
    const newId = Date.now();
    setSelectedMacro(newId);
    setMacroRecords([]);
    setMoveDrafts({});
    setDelayMode('none');
    setMacroLoopMode('once');
    setMacroLoopCount(MACRO_LOOP_COUNT_MIN);
    setSelectedRecords([]);
    setSelectedDelayIds([]);
    setSelectedKeyIds([]);
    delaySelectAnchorRef.current = -1;
    keySelectAnchorRef.current = -1;
    setEditingRecordId(null);
    setIsMacroRecording(false);
    setContextMenu(null);
    setShowAddEventMenu(false);
    const localCount = macros.filter((macro) => !macro.fromDevice).length;
    setMacroDraftName(`本地宏 ${localCount + 1}`);
    setMacroDraftType('record');
    setMacroIsNew(true);
    setMacroWizardStep('name');
    setMacroEditorOpen(true);
  };

  const openMacroEditor = (macroId) => {
    const macro = macros.find(m => m.id === macroId);
    if (!macro) return;
    setSelectedMacro(macroId);
    setMacroRecords(normalizeDelayRecords(macro.records || []));
    setMoveDrafts({});
    // 仅保留 none / dynamic；历史 fixed 回落为 dynamic
    setDelayMode(macro.delayMode === 'dynamic' ? 'dynamic' : (macro.delayMode === 'none' ? 'none' : 'dynamic'));
    setMacroLoopMode(macro.loopMode || 'once');
    setMacroLoopCount(clampMacroLoopCount(macro.loopCount));
    setSelectedRecords([]);
    setSelectedDelayIds([]);
    setSelectedKeyIds([]);
    delaySelectAnchorRef.current = -1;
    keySelectAnchorRef.current = -1;
    setEditingRecordId(null);
    setIsMacroRecording(false);
    setContextMenu(null);
    setShowAddEventMenu(false);
    setMacroDraftName(macro.name || '');
    setMacroDraftType(macro.macroType || 'record');
    setMacroIsNew(false);
    setMacroWizardStep('edit');
    setMacroEditorOpen(true);
    setDragOverIndex(null);
    setIsDraggingRecords(false);
    setMarqueeRect(null);
    lastSelectedIndexRef.current = -1;
    selectionAnchorRef.current = -1;
  };

  const isWorkspaceEditorVisible =
    macroWorkspace && !macroIsNew && macros.length > 0;

  useEffect(() => {
    if (
      macroWorkspace &&
      !macroEditorOpen &&
      !macroIsNew &&
      macros.length > 0
    ) {
      const macroId =
        macros.some((macro) => macro.id === selectedMacro)
          ? selectedMacro
          : macros[0].id;
      openMacroEditor(macroId);
    }
    // openMacroEditor intentionally stays local to the macro editor state machine.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [macroWorkspace, macroEditorOpen, macroIsNew, macros, selectedMacro]);

  const closeMacroEditor = () => {
    if (isMacroRecording) setIsMacroRecording(false);

    const wasNew = macroIsNew;

    // 已在列表中的宏：关闭时同步本地编辑草稿（列表仍只展示已保存项的 fromDevice 状态）
    if (selectedMacro && !wasNew) {
      setMacros(prev => prev.map(m =>
        m.id === selectedMacro
          ? {
              ...m,
              records: normalizeDelayRecords(macroRecords),
              delayMode,
              loopMode: macroLoopMode,
              loopCount: clampMacroLoopCount(macroLoopCount),
              name: macroDraftName.trim() || m.name,
              macroType: macroDraftType,
            }
          : m
      ));
    }

    // 新建未保存：直接丢弃，不进列表
    setMacroEditorOpen(false);
    setMacroWizardStep('edit');
    setMacroIsNew(false);
    setContextMenu(null);
    setShowAddEventMenu(false);
    setEditingRecordId(null);
    if (wasNew) {
      setSelectedMacro(null);
      setMacroRecords([]);
      setMacroDraftName('');
    }
  };

  const confirmMacroNameStep = () => {
    const name = macroDraftName.trim();
    if (!name) {
      toast.warning('请输入宏名称');
      return;
    }
    setMacroDraftName(name);
    setMacroWizardStep('edit');
  };


  const deleteMacro = async (macroId) => {
    // 找到要删除的宏
    const macroToDelete = macros.find(m => m.id === macroId);
    if (!macroToDelete) return;

    // 如果宏未提交到设备，只从本地删除
    if (!macroToDelete.fromDevice) {
      const remainingMacros = macros.filter(m => m.id !== macroId);
      setMacros(remainingMacros);
      if (selectedMacro === macroId) {
        setSelectedMacro(null);
        setMacroRecords([]);
        setMacroEditorOpen(false);
        setIsMacroRecording(false);
      }
      toast.success('宏已删除');
      return;
    }

    // 获取宏的设备序号
    const macroIndex = macroToDelete.macroIndex ?? macros.findIndex(m => m.id === macroId);

    // 检查是否有按键绑定了这个宏，如果有则恢复默认值
    if (isConnected && sender && mouseButtonData?.parsed) {
      const parsed = mouseButtonData.parsed;
      // 按键名到 parsed 属性的映射
      const buttonParsedKeys = {
        'left': 'leftButton',
        'right': 'rightButton',
        'middle': 'middleButton',
        'button4': 'button4',
        'button5': 'button5',
        'dpi': 'dpiButton',
        'reportRate': 'reportRateButton',
        'pair': 'pairButton',
        'nc1': 'nc1',
        'nc2': 'nc2',
        'scrollUp': 'scrollUp',
        'scrollDown': 'scrollDown'
      };

      // 遍历所有按键，检查是否绑定了被删除的宏
      for (const [buttonName, parsedKey] of Object.entries(buttonParsedKeys)) {
        const buttonValue = parsed[parsedKey];
        const binding = parseMacroBindingValue(buttonValue);
        if (binding && binding.macroIndex === macroIndex) {
          const buttonIndex = BUTTON_INDEX_MAP[buttonName];
          const defaultValue = BUTTON_DEFAULT_VALUES[buttonName];
          if (buttonIndex !== undefined && defaultValue) {
            try {
              console.log(`宏被删除，恢复按键 ${buttonName}(序号${buttonIndex}) 为默认值: ${defaultValue}`);
              await sender.setButtonMapping(buttonIndex, defaultValue);
            } catch (error) {
              console.error(`恢复按键 ${buttonName} 默认值失败:`, error);
            }
          }
        }
      }

      // 等待一下并刷新按键数据
      await sender.wait(100);
      await sender.getMouseButtonData();
    }

    // 计算删除后的宏列表
    const remainingMacros = macros.filter(m => m.id !== macroId);
    const remainingOnboardMacros = remainingMacros.filter((macro) => macro.fromDevice);

    // 如果设备已连接，同步删除后的宏数据到设备
    if (isConnected && sender && dataStore) {
      try {
        if (remainingOnboardMacros.length > 0) {
          // 构建所有剩余宏对象（保留原有序号，不重新分配）
          const macroObjects = remainingOnboardMacros.map((macro, index) =>
            buildMacroObject(macro, macro.macroIndex !== undefined ? macro.macroIndex : index)
          );
          // 使用 dataStore 构建宏数据
          const macroData = dataStore.buildMacroData(macroObjects);
          console.log('删除宏后发送更新的宏数据:', macroData.length, '字节');
          await sender.setMacroData(macroData);
        } else {
          // 如果没有宏了，发送空宏数据
          const emptyMacroData = new Uint8Array([0, 0]); // 长度为0
          console.log('删除所有宏，发送空宏数据');
          await sender.setMacroData(emptyMacroData);
        }
        await sender.wait(100);
        await sender.getMacroData();
        toast.success('宏已删除');
      } catch (error) {
        console.error('同步宏数据到设备失败:', error);
        toast.error('同步宏数据失败: ' + error.message);
      }
    }

    // 更新本地宏列表（保留原有 macroIndex，不重新分配）
    setMacros(remainingMacros);
    if (selectedMacro === macroId) {
      setSelectedMacro(null);
      setMacroRecords([]);
      setMacroEditorOpen(false);
      setIsMacroRecording(false);
    }
  };

  const cloneMacro = (macroId) => {
    const source = macros.find((macro) => String(macro.id) === String(macroId));
    if (!source) {
      toast.error('未找到要复制的宏');
      return;
    }
    const clone = {
      ...source,
      id: `local-clone-${Date.now()}`,
      name: uniqueCloneName(
        source.name || '宏',
        macros.map((macro) => macro.name)
      ),
      fromDevice: false,
      source: 'local',
      macroIndex: undefined,
      rawData: undefined,
      records: cloneMacroRecords(source.records),
    };
    setMacros((prev) => [...prev, clone]);
    setLocalMacrosOpen(true);
    setMacroListMenu(null);
    toast.success(`已复制为“${clone.name}”`);
  };

  const openMacroListMenu = (event, macroId, source) => {
    event.stopPropagation();
    setContextMenu(null);
    if (macroListMenu?.macroId === macroId) {
      setMacroListMenu(null);
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    setMacroListMenu({
      macroId,
      source,
      x: rect.left,
      y: rect.bottom + FLOATING_MENU_GAP,
      anchor: {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
    });
  };

  // 开始编辑宏名称
  const startRenameMacro = (macroId, currentName) => {
    setEditingMacroName(macroId);
    setTempMacroName(currentName);
  };

  // 取消编辑宏名称
  const cancelRenameMacro = () => {
    setEditingMacroName(null);
    setTempMacroName('');
  };

  // 确认修改宏名称并发送到设备
  const confirmRenameMacro = async (macroId) => {
    if (!tempMacroName.trim()) {
      toast.warning('宏名称不能为空');
      return;
    }

    // 更新本地宏列表
    const updatedMacros = macros.map(m =>
      m.id === macroId ? { ...m, name: tempMacroName.trim() } : m
    );
    setMacros(updatedMacros);
    setEditingMacroName(null);

    const renamedMacro = updatedMacros.find((macro) => macro.id === macroId);
    if (renamedMacro && !renamedMacro.fromDevice) {
      toast.success('本地宏名称已更新');
      return;
    }

    // 发送到设备
    if (!isConnected || !sender || !dataStore) {
      toast.warning('设备未连接，名称仅在本地保存');
      return;
    }

    try {
      // 构建所有宏对象
      const onboardMacros = updatedMacros.filter((macro) => macro.fromDevice);
      const macroObjects = onboardMacros.map((macro, index) =>
        buildMacroObject(macro, macro.macroIndex !== undefined ? macro.macroIndex : index)
      );

      // 使用 dataStore 构建宏数据
      const macroData = dataStore.buildMacroData(macroObjects);
      console.log('发送重命名后的宏数据:', macroData.length, '字节');

      // 发送到设备
      await sender.setMacroData(macroData);
      await sender.wait(200);
      await sender.getMacroData(1);

      toast.success('宏名称已更新');
    } catch (error) {
      console.error('更新宏名称失败:', error);
      toast.error('更新失败: ' + error.message);
    }
  };

  // 处理宏名称输入框按键事件
  const handleMacroNameKeyDown = (e, macroId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirmRenameMacro(macroId);
    } else if (e.key === 'Escape') {
      cancelRenameMacro();
    }
  };

  const selectMacro = (macroId) => {
    setSelectedMacro(macroId);
    const macro = macros.find(m => m.id === macroId);
    if (macro) {
      setMacroRecords(normalizeDelayRecords(macro.records || []));
      setDelayMode(macro.delayMode === 'none' ? 'none' : 'dynamic');
      // 注意：循环设置不在这里加载，它跟着选中的按键走（见 getSelectedButtonMacroInfo）
      setSelectedRecords([]);
      setSelectedDelayIds([]);
      setSelectedKeyIds([]);
      delaySelectAnchorRef.current = -1;
      keySelectAnchorRef.current = -1;
      lastSelectedIndexRef.current = -1;
      selectionAnchorRef.current = -1;
    }
  };

  const toggleMacroRecording = () => {
    if (isMacroRecording) {
      // 停止录制，只保存记录（循环设置由绑定面板单独下发，这里原样保留）
      setIsMacroRecording(false);
      if (selectedMacro) {
        setMacros(macros.map(m =>
          m.id === selectedMacro
            ? { ...m, records: macroRecords, delayMode }
            : m
        ));
      }
    } else {
      // 开始录制
      if (!selectedMacro) {
        toast.warning('请先选择或创建一个宏');
        return;
      }
      setIsMacroRecording(true);
    }
  };

  const addMacroRecord = (eventType = 'down') => {
    let newRecord;

    if (eventType === 'delay') {
      const previous = macroRecords[macroRecords.length - 1];
      if (!previous) {
        toast.warning('请先添加一个按键事件，再设置该事件的延迟');
        setShowAddEventMenu(false);
        return;
      }
      if (previous.type === 'move') {
        toast.warning(`鼠标移动事件固定 ${MACRO_MOUSE_MOVE_DELAY_MS}ms 延时，无法单独设置`);
        setShowAddEventMenu(false);
        return;
      }
      if (previous.type === 'delay') {
        setSelectedDelayIds([previous.id]);
        setEditingRecordId(previous.id);
        toast.warning('当前事件已有延迟，请直接修改现有值');
        setShowAddEventMenu(false);
        return;
      }
      newRecord = {
        id: Date.now(),
        type: 'delay',
        keyName: '延时',
        delay: 100,
        action: 'delay'
      };
    } else if (eventType === 'mouse') {
      newRecord = createMouseButtonRecord(Date.now());
    } else if (eventType === 'move') {
      newRecord = createMoveRecord(Date.now());
    } else if (AXIS_RECORD_EVENT_TYPES[eventType]) {
      newRecord = createAxisRecord(Date.now(), AXIS_RECORD_EVENT_TYPES[eventType]);
    } else {
      newRecord = {
        id: Date.now(),
        type: 'key',
        keyCode: null,
        keyName: '',
        modifiers: [],
        delay: 0,
        action: eventType === 'up' ? 'up' : 'down',
        eventType: 1
      };
    }

    const nextRecords = [...macroRecords, newRecord];
    setMacroRecords(nextRecords);
    setEditingRecordId(newRecord.id);
    if (newRecord.type === 'key') {
      setSelectedKeyIds([newRecord.id]);
      keySelectAnchorRef.current = nextRecords.filter((r) => r.type === 'key').length - 1;
      clearDelayInputSelection();
    } else {
      clearKeyInputSelection();
    }
    setShowAddEventMenu(false);
  };

  const editingRecordIdRef = useRef(null);
  useEffect(() => { editingRecordIdRef.current = editingRecordId; }, [editingRecordId]);

  // 当前可被赋值的按键记录目标（多选优先，其次 editingRecordId）
  const getKeyAssignTargetIds = () => {
    const selected = selectedKeyIdsRef.current;
    if (selected.length > 0) return selected;
    const editId = editingRecordIdRef.current;
    if (editId != null) {
      const rec = macroRecordsRef.current.find((r) => r.id === editId);
      if (rec && rec.type === 'key') return [editId];
    }
    return [];
  };

  // 批量/单条设置按键（字符面板 / 键盘监听共用）
  const setRecordKey = (keyCode, keyName) => {
    const targetIds = getKeyAssignTargetIds();
    if (!targetIds.length) return;

    const idSet = new Set(targetIds);
    const isModifier = keyCode >= 0xE0 && keyCode <= 0xE7;
    const name = keyName || resolveHidKeyName(keyCode);
    setMacroRecords((prev) =>
      prev.map((r) => {
        if (r.type !== 'key' || !idSet.has(r.id)) return r;
        return {
          ...r,
          keyCode,
          keyName: name,
          value: keyCode,
          eventType: isModifier ? 2 : 1,
        };
      })
    );
  };

  const setRecordKeyRef = useRef(setRecordKey);
  useEffect(() => { setRecordKeyRef.current = setRecordKey; });

  // 延时 / 按键 序列（仅对应类型，用于输入框 Shift 连选）
  const getDelaySequence = () =>
    macroRecordsRef.current
      .map((r, i) => ({ id: r.id, type: r.type, index: i }))
      .filter((r) => r.type === 'delay');

  const getKeySequence = () =>
    macroRecordsRef.current
      .map((r, i) => ({ id: r.id, type: r.type, index: i }))
      .filter((r) => r.type === 'key');

  // 通用输入框多选：Ctrl/⌘ 点选，Shift 范围，单击独占
  const applyFieldMultiSelect = ({
    recordId,
    e,
    sequence,
    anchorRef,
    selectedRef,
    setSelected,
    onExclusive,
  }) => {
    e.stopPropagation();
    const seqIndex = sequence.findIndex((d) => d.id === recordId);
    if (seqIndex < 0) return;

    const isToggle = e.metaKey || e.ctrlKey;
    const isRange = e.shiftKey;

    if (isRange && anchorRef.current >= 0) {
      const start = Math.min(anchorRef.current, seqIndex);
      const end = Math.max(anchorRef.current, seqIndex);
      const rangeIds = sequence.slice(start, end + 1).map((d) => d.id);
      if (isToggle) {
        setSelected((prev) => {
          const set = new Set(prev);
          rangeIds.forEach((id) => set.add(id));
          return Array.from(set);
        });
      } else {
        setSelected(rangeIds);
      }
      return;
    }

    if (isToggle) {
      setSelected((prev) =>
        prev.includes(recordId)
          ? prev.filter((id) => id !== recordId)
          : [...prev, recordId]
      );
      anchorRef.current = seqIndex;
      return;
    }

    // 普通单击：若已在多选组内则保持组，便于直接改值；否则独占
    const current = selectedRef.current;
    if (current.length > 1 && current.includes(recordId)) {
      anchorRef.current = seqIndex;
      if (onExclusive) onExclusive(recordId, seqIndex, true);
      return;
    }

    setSelected([recordId]);
    anchorRef.current = seqIndex;
    if (onExclusive) onExclusive(recordId, seqIndex, false);
  };

  // 延时输入框多选
  const handleDelayInputSelect = (recordId, e) => {
    clearKeyInputSelection();
    applyFieldMultiSelect({
      recordId,
      e,
      sequence: getDelaySequence(),
      anchorRef: delaySelectAnchorRef,
      selectedRef: selectedDelayIdsRef,
      setSelected: setSelectedDelayIds,
    });
  };

  // 按键输入框多选
  const handleKeyInputSelect = (recordId, e) => {
    clearDelayInputSelection();
    applyFieldMultiSelect({
      recordId,
      e,
      sequence: getKeySequence(),
      anchorRef: keySelectAnchorRef,
      selectedRef: selectedKeyIdsRef,
      setSelected: setSelectedKeyIds,
      onExclusive: (id) => {
        setEditingRecordId(id);
      },
    });
  };

  // 批量/单条设置延时值（基于延时输入框多选，与行复选框无关）
  const updateDelayValue = (recordId, rawValue) => {
    const value = Math.max(0, Math.min(65535, parseInt(rawValue, 10) || 0));
    const selected = selectedDelayIdsRef.current;
    const multiEdit = selected.length > 1 && selected.includes(recordId);

    setMacroRecords((prev) =>
      prev.map((r) => {
        if (r.type !== 'delay') return r;
        if (multiEdit ? selected.includes(r.id) : r.id === recordId) {
          return { ...r, delay: value, value, keyName: `${value}ms` };
        }
        return r;
      })
    );
  };

  const clearDelayInputSelection = () => {
    setSelectedDelayIds([]);
    delaySelectAnchorRef.current = -1;
  };

  const clearKeyInputSelection = () => {
    setSelectedKeyIds([]);
    keySelectAnchorRef.current = -1;
  };

  const clearFieldInputSelections = () => {
    clearDelayInputSelection();
    clearKeyInputSelection();
  };

  // 将选中项整体移动到 toIndex 前（Windows 式拖拽）
  const moveSelectedRecordsTo = (toIndex, draggedIds) => {
    const ids = draggedIds && draggedIds.length ? draggedIds : selectedRecordsRef.current;
    if (!ids.length) return;

    const records = macroRecordsRef.current;
    const idSet = includeOwnedDelays(records, ids);
    const moving = records.filter((r) => idSet.has(r.id));
    if (!moving.length) return;

    const remaining = [];
    const oldIndexes = [];
    records.forEach((r, i) => {
      if (idSet.has(r.id)) oldIndexes.push(i);
      else remaining.push(r);
    });

    // 目标索引映射到 remaining 坐标系
    let insertAt = toIndex;
    const removedBefore = oldIndexes.filter((i) => i < toIndex).length;
    insertAt = Math.max(0, Math.min(remaining.length, toIndex - removedBefore));

    const next = [
      ...remaining.slice(0, insertAt),
      ...moving,
      ...remaining.slice(insertAt),
    ];
    const normalizedNext = normalizeDelayRecords(next);
    const remainingIds = new Set(normalizedNext.map((record) => record.id));
    setMacroRecords(normalizedNext);
    setSelectedRecords(moving.map((r) => r.id).filter((id) => remainingIds.has(id)));
  };

  const deleteMacroRecords = () => {
    const remove = includeOwnedDelays(macroRecords, selectedRecords);
    setMacroRecords(normalizeDelayRecords(macroRecords.filter(r => !remove.has(r.id))));
    setSelectedRecords([]);
    setSelectedDelayIds((prev) => prev.filter((id) => !remove.has(id)));
    setSelectedKeyIds((prev) => prev.filter((id) => !remove.has(id)));
    lastSelectedIndexRef.current = -1;
    selectionAnchorRef.current = -1;
  };

  // 删除单个记录
  const deleteRecord = (recordId) => {
    const remove = includeOwnedDelays(macroRecords, [recordId]);
    setMacroRecords(normalizeDelayRecords(macroRecords.filter(r => !remove.has(r.id))));
    setSelectedRecords((prev) => prev.filter((id) => id !== recordId));
    setSelectedDelayIds((prev) => prev.filter((id) => id !== recordId));
    setSelectedKeyIds((prev) => prev.filter((id) => id !== recordId));
    if (editingRecordId === recordId) setEditingRecordId(null);
    setContextMenu(null);
  };

  // 插入记录
  const insertRecord = (recordId, position, type) => {
    const index = macroRecords.findIndex(r => r.id === recordId);
    if (index === -1) return;

    let insertIndex = position === 'above' ? index : index + 1;
    if (type === 'delay') {
      const ownerIndex = position === 'above' ? index - 1 : index;
      const owner = macroRecords[ownerIndex];
      if (!owner || owner.type === 'delay') {
        toast.warning('延迟必须放在一个按键事件之后');
        setContextMenu(null);
        return;
      }

      if (owner.type === 'move') {
        toast.warning(`鼠标移动事件固定 ${MACRO_MOUSE_MOVE_DELAY_MS}ms 延时，无法单独设置`);
        setContextMenu(null);
        return;
      }

      insertIndex = ownerIndex + 1;
      const existingDelay = macroRecords[insertIndex];
      if (existingDelay?.type === 'delay') {
        setSelectedDelayIds([existingDelay.id]);
        setEditingRecordId(existingDelay.id);
        toast.warning('该事件已有延迟，请直接修改现有值');
        setContextMenu(null);
        return;
      }
    }

    let newRecord;
    if (type === 'move') {
      newRecord = createMoveRecord(Date.now());
    } else if (type === 'mouse') {
      newRecord = createMouseButtonRecord(Date.now());
    } else if (AXIS_RECORD_EVENT_TYPES[type]) {
      newRecord = createAxisRecord(Date.now(), AXIS_RECORD_EVENT_TYPES[type]);
    } else {
      newRecord = {
        id: Date.now(),
        type: type === 'delay' ? 'delay' : 'key',
        keyCode: null,
        keyName: type === 'delay' ? '延时' : '',
        modifiers: [],
        delay: type === 'delay' ? 100 : 0,
        action: type === 'up' ? 'up' : 'down',
        eventType: type === 'delay' ? 0 : 1,
      };
    }

    const newRecords = [...macroRecords];
    newRecords.splice(insertIndex, 0, newRecord);
    setMacroRecords(newRecords);
    setEditingRecordId(newRecord.id);
    if (newRecord.type === 'key') {
      setSelectedKeyIds([newRecord.id]);
      keySelectAnchorRef.current = newRecords.filter((r) => r.type === 'key').findIndex((r) => r.id === newRecord.id);
      clearDelayInputSelection();
    } else {
      clearKeyInputSelection();
    }
    setContextMenu(null);
  };

  // 打开右键菜单
  const openContextMenu = (e, recordId) => {
    e.stopPropagation();
    setMacroListMenu(null);
    const rect = e.currentTarget.getBoundingClientRect();
    setContextMenu({
      recordId,
      x: rect.left,
      y: rect.bottom + FLOATING_MENU_GAP,
      anchor: {
        left: rect.left,
        right: rect.right,
        top: rect.top,
        bottom: rect.bottom,
        width: rect.width,
        height: rect.height,
      },
      submenu: null
    });
  };

  // 关闭右键菜单
  const closeContextMenu = () => {
    setContextMenu(null);
  };

  // 点击外部关闭右键菜单
  useEffect(() => {
    if (!contextMenu) return;
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu]);

  useEffect(() => {
    if (!macroListMenu) return;
    const handleClickOutside = () => setMacroListMenu(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [macroListMenu]);

  useLayoutEffect(() => {
    const menu = macroListMenuRef.current;
    if (!macroListMenu || !menu) {
      setMacroListMenuPos(null);
      return;
    }
    const fitted = fitMenuBelowAnchor(
      macroListMenu.anchor,
      menu.offsetWidth,
      menu.offsetHeight || naturalMenuHeight(menu)
    );
    setMacroListMenuPos({
      ...viewportToFixed(menu, fitted.left, fitted.top),
      maxHeight: fitted.maxHeight,
    });
  }, [macroListMenu]);

  // 点击外部关闭添加事件菜单
  useEffect(() => {
    if (!showAddEventMenu) return;
    const handleClickOutside = (e) => {
      if (!e.target.closest('.macro-add-record-wrapper')) {
        setShowAddEventMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showAddEventMenu]);

  /*
   * “添加事件”菜单默认向上展开，但它挂在会滚动的 .macro-editor-body 里，
   * 窗口不够高时整块被容器裁掉、下面几类事件点不到。改成固定定位后，
   * 这里在浏览器绘制前量出菜单实际高度，再决定向上还是向下展开、是否限高。
   */
  useLayoutEffect(() => {
    if (!showAddEventMenu) {
      setAddEventMenuPos(null);
      return undefined;
    }
    const layout = () => {
      const button = addEventButtonRef.current;
      const menu = addEventMenuRef.current;
      if (!button || !menu) return;
      const anchor = button.getBoundingClientRect();
      const { top, maxHeight } = fitMenuAboveAnchor(anchor, naturalMenuHeight(menu));
      const left = clampToViewport(anchor.left, anchor.width, window.innerWidth);
      setAddEventMenuPos({
        ...viewportToFixed(menu, left, top),
        width: anchor.width,
        maxHeight,
      });
    };
    layout();
    window.addEventListener('resize', layout);
    // 捕获阶段：内层滚动容器（宏记录列表、编辑区）滚动时也要跟随
    window.addEventListener('scroll', layout, true);
    return () => {
      window.removeEventListener('resize', layout);
      window.removeEventListener('scroll', layout, true);
    };
  }, [showAddEventMenu, macroEventMenuGroups]);

  // 三点菜单挂在应用根层后按按钮位置贴边展开，窄/矮窗口也要能看见
  useLayoutEffect(() => {
    const menu = contextMenuRef.current;
    if (!contextMenu || !menu) {
      setContextMenuPos(null);
      return;
    }
    const anchor = contextMenu.anchor || {
      left: contextMenu.x,
      right: contextMenu.x,
      top: contextMenu.y - FLOATING_MENU_GAP,
      bottom: contextMenu.y - FLOATING_MENU_GAP,
      width: 0,
      height: 0,
    };
    const fitted = fitMenuBelowAnchor(
      anchor,
      menu.offsetWidth,
      menu.offsetHeight || naturalMenuHeight(menu)
    );
    setContextMenuPos({
      ...viewportToFixed(menu, fitted.left, fitted.top),
      maxHeight: fitted.maxHeight,
    });
  }, [contextMenu]);

  // “向上/向下插入”子菜单与添加菜单同款内容，靠近视口底部时也要收进来
  useLayoutEffect(() => {
    const submenu = contextSubmenuRef.current;
    if (!contextMenu?.submenu || !submenu) {
      setContextSubmenuPos(null);
      return;
    }
    const anchor = submenu.parentElement.getBoundingClientRect();
    const menuHeight = naturalMenuHeight(submenu);
    const width = submenu.offsetWidth;
    const room = Math.max(
      window.innerHeight - FLOATING_MENU_EDGE * 2,
      FLOATING_MENU_MIN_HEIGHT
    );
    const maxHeight = menuHeight > 0 ? Math.min(menuHeight, room) : room;
    // 子菜单默认贴在右侧，右边放不下就翻到左侧
    const right = anchor.right + FLOATING_MENU_GAP;
    const openRight = right + width <= window.innerWidth - FLOATING_MENU_EDGE;
    const viewLeft = openRight
      ? right
      : clampToViewport(anchor.left - FLOATING_MENU_GAP - width, width, window.innerWidth);
    setContextSubmenuPos({
      ...viewportToFixed(
        submenu,
        viewLeft,
        // 与被悬停项对齐（-6px 抵消菜单自身的上内边距），再夹回视口
        clampToViewport(anchor.top - 6, maxHeight, window.innerHeight)
      ),
      maxHeight,
    });
  }, [contextMenu, macroEventMenuGroups]);

  // 宏按键赋值：选中/编辑按键输入框时监听用户键盘，写入选中目标
  useEffect(() => {
    if (!macroEditorOpen || macroWizardStep !== 'edit') return;
    if (isMacroRecording) return;

    const hasTarget =
      selectedKeyIds.length > 0 ||
      (editingRecordId != null &&
        macroRecordsRef.current.some((r) => r.id === editingRecordId && r.type === 'key'));
    if (!hasTarget) return;

    const onKeyDown = (e) => {
      // 延时数字输入中不抢键
      if (e.target && e.target.classList && e.target.classList.contains('delay-input-field')) {
        return;
      }
      // 其它可编辑框（如重命名）
      const tag = (e.target && e.target.tagName) || '';
      if (
        (tag === 'INPUT' || tag === 'TEXTAREA') &&
        e.target &&
        !e.target.classList.contains('record-input') &&
        !e.target.readOnly
      ) {
        return;
      }

      const hidCode = JS_KEY_TO_HID[e.code];
      console.log('[宏按键监听]', {
        key: e.key,
        code: e.code,
        value: hidCode ?? null,
      });
      if (!hidCode) return;

      e.preventDefault();
      e.stopPropagation();
      setRecordKeyRef.current(hidCode, resolveHidKeyName(hidCode));
    };

    // capture 优先，避免被列表快捷键抢走
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [macroEditorOpen, macroWizardStep, isMacroRecording, selectedKeyIds, editingRecordId]);

  // 宏记录快捷键：Ctrl/⌘A 全选，Delete/Backspace 删除选中
  // （按键赋值监听开启时，普通按键由赋值监听处理，此处只保留选择类快捷键）
  useEffect(() => {
    if (!macroEditorOpen || macroWizardStep !== 'edit') return;
    if (isMacroRecording) return;

    const keyAssignActive =
      selectedKeyIdsRef.current.length > 0 ||
      (editingRecordId != null &&
        macroRecordsRef.current.some((r) => r.id === editingRecordId && r.type === 'key'));

    const onKeyDown = (e) => {
      const tag = (e.target && e.target.tagName) || '';
      const isTyping =
        tag === 'INPUT' ||
        tag === 'TEXTAREA' ||
        tag === 'SELECT' ||
        (e.target && e.target.isContentEditable);

      // 赋值模式：Escape 退出按键编辑选区
      if (keyAssignActive && e.key === 'Escape') {
        e.preventDefault();
        clearKeyInputSelection();
        setEditingRecordId(null);
        return;
      }

      if ((e.metaKey || e.ctrlKey) && (e.key === 'a' || e.key === 'A')) {
        if (isTyping || keyAssignActive) return;
        e.preventDefault();
        const ids = macroRecordsRef.current.map((r) => r.id);
        setSelectedRecords(ids);
        setSelectedDelayIds([]);
        setSelectedKeyIds([]);
        delaySelectAnchorRef.current = -1;
        keySelectAnchorRef.current = -1;
        selectionAnchorRef.current = 0;
        lastSelectedIndexRef.current = ids.length > 0 ? ids.length - 1 : -1;
        return;
      }

      // 赋值模式下 Backspace/Delete 作为可录入键，不删行
      if ((e.key === 'Delete' || e.key === 'Backspace') && !isTyping && !keyAssignActive) {
        if (selectedRecordsRef.current.length === 0) return;
        e.preventDefault();
        const remove = includeOwnedDelays(
          macroRecordsRef.current,
          selectedRecordsRef.current
        );
        setMacroRecords((prev) =>
          normalizeDelayRecords(prev.filter((r) => !remove.has(r.id)))
        );
        setSelectedRecords([]);
        setSelectedDelayIds((prev) => prev.filter((id) => !remove.has(id)));
        setSelectedKeyIds((prev) => prev.filter((id) => !remove.has(id)));
        lastSelectedIndexRef.current = -1;
        selectionAnchorRef.current = -1;
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [macroEditorOpen, macroWizardStep, isMacroRecording, selectedKeyIds, editingRecordId]);

  const selectAllRecords = () => {
    if (selectedRecords.length === macroRecords.length && macroRecords.length > 0) {
      setSelectedRecords([]);
      lastSelectedIndexRef.current = -1;
      selectionAnchorRef.current = -1;
    } else {
      setSelectedRecords(macroRecords.map(r => r.id));
      lastSelectedIndexRef.current = macroRecords.length > 0 ? macroRecords.length - 1 : -1;
      selectionAnchorRef.current = 0;
    }
  };

  // Windows 式选择：单击 / Ctrl·⌘ 点选 / Shift 范围（行选择，与输入框多选独立）
  const handleRecordSelect = (recordId, index, e) => {
    // 操作行选择时收起输入框多选，避免两套选区混淆
    clearFieldInputSelections();

    const isToggle = e.metaKey || e.ctrlKey;
    const isRange = e.shiftKey;

    if (isRange && selectionAnchorRef.current >= 0) {
      const start = Math.min(selectionAnchorRef.current, index);
      const end = Math.max(selectionAnchorRef.current, index);
      const rangeIds = macroRecords.slice(start, end + 1).map((r) => r.id);
      if (isToggle) {
        setSelectedRecords((prev) => {
          const set = new Set(prev);
          rangeIds.forEach((id) => set.add(id));
          return Array.from(set);
        });
      } else {
        setSelectedRecords(rangeIds);
      }
      lastSelectedIndexRef.current = index;
      return;
    }

    if (isToggle) {
      setSelectedRecords((prev) =>
        prev.includes(recordId)
          ? prev.filter((id) => id !== recordId)
          : [...prev, recordId]
      );
      selectionAnchorRef.current = index;
      lastSelectedIndexRef.current = index;
      return;
    }

    setSelectedRecords([recordId]);
    selectionAnchorRef.current = index;
    lastSelectedIndexRef.current = index;
  };

  const isInteractiveTarget = (target) => {
    if (!target || !target.closest) return false;
    return Boolean(
      target.closest('input, select, textarea, button, a, .record-select, .delay-input-field, .control-btn')
    );
  };

  // 行 mousedown：开始拖拽排序或为后续 click 做准备
  const handleRecordMouseDown = (e, record, index) => {
    if (e.button !== 0) return;
    if (isInteractiveTarget(e.target)) return;

    const alreadySelected = selectedRecordsRef.current.includes(record.id);
    let dragIds = selectedRecordsRef.current;

    // 未选中且无修饰键：先单选再拖；已多选则整体拖
    if (!alreadySelected && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
      dragIds = [record.id];
      setSelectedRecords([record.id]);
      selectionAnchorRef.current = index;
      lastSelectedIndexRef.current = index;
    } else if (!alreadySelected && (e.metaKey || e.ctrlKey) && !e.shiftKey) {
      // Ctrl 点未选项：先加入选择
      dragIds = [...selectedRecordsRef.current, record.id];
      setSelectedRecords(dragIds);
      selectionAnchorRef.current = index;
      lastSelectedIndexRef.current = index;
    }

    const startX = e.clientX;
    const startY = e.clientY;
    const session = {
      startX,
      startY,
      fromIndex: index,
      recordId: record.id,
      dragIds: [...dragIds],
      dragging: false,
      skipClickSelect: alreadySelected && !e.shiftKey && !e.metaKey && !e.ctrlKey,
    };
    dragSessionRef.current = session;

    const onMove = (ev) => {
      const s = dragSessionRef.current;
      if (!s) return;
      const dx = ev.clientX - s.startX;
      const dy = ev.clientY - s.startY;
      if (!s.dragging && (Math.abs(dx) > 4 || Math.abs(dy) > 4)) {
        s.dragging = true;
        setIsDraggingRecords(true);
        suppressClickRef.current = true;
      }
      if (!s.dragging) return;

      const listEl = recordsListRef.current;
      if (!listEl) return;
      const rows = Array.from(listEl.querySelectorAll('.record-row'));
      let insertIndex = rows.length;
      for (let i = 0; i < rows.length; i++) {
        const rect = rows[i].getBoundingClientRect();
        const mid = rect.top + rect.height / 2;
        if (ev.clientY < mid) {
          insertIndex = i;
          break;
        }
      }
      setDragOverIndex(insertIndex);
    };

    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const s = dragSessionRef.current;
      dragSessionRef.current = null;

      if (s?.dragging) {
        const listEl = recordsListRef.current;
        let insertIndex = macroRecordsRef.current.length;
        if (listEl) {
          const rows = Array.from(listEl.querySelectorAll('.record-row'));
          insertIndex = rows.length;
          for (let i = 0; i < rows.length; i++) {
            const rect = rows[i].getBoundingClientRect();
            const mid = rect.top + rect.height / 2;
            if (ev.clientY < mid) {
              insertIndex = i;
              break;
            }
          }
        }
        moveSelectedRecordsTo(insertIndex, s.dragIds);
        setIsDraggingRecords(false);
        setDragOverIndex(null);
        // 短暂抑制随后的 click
        setTimeout(() => { suppressClickRef.current = false; }, 0);
      } else {
        setIsDraggingRecords(false);
        setDragOverIndex(null);
      }
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleRecordClick = (e, record, index) => {
    if (suppressClickRef.current) {
      e.preventDefault();
      return;
    }
    if (isInteractiveTarget(e.target)) return;
    // 在已选多选块上按下未拖动：click 时改为仅选中该项（Windows 桌面行为）
    handleRecordSelect(record.id, index, e);
  };

  // 空白处框选（像资源管理器）
  const handleRecordsListMouseDown = (e) => {
    if (e.button !== 0) return;
    if (e.target.closest('.record-row')) return;
    if (!recordsListRef.current) return;

    const listEl = recordsListRef.current;
    const listRect = listEl.getBoundingClientRect();
    const startX = e.clientX;
    const startY = e.clientY;
    const additive = e.metaKey || e.ctrlKey;
    const baseSelection = additive ? [...selectedRecordsRef.current] : [];

    if (!additive) {
      setSelectedRecords([]);
      lastSelectedIndexRef.current = -1;
      selectionAnchorRef.current = -1;
      clearFieldInputSelections();
    }

    marqueeSessionRef.current = {
      startX,
      startY,
      listScrollTop: listEl.scrollTop,
      additive,
      baseSelection,
      active: false,
    };

    const toLocal = (clientX, clientY) => ({
      x: clientX - listRect.left + listEl.scrollLeft,
      y: clientY - listRect.top + listEl.scrollTop,
    });

    const onMove = (ev) => {
      const s = marqueeSessionRef.current;
      if (!s) return;
      const dx = ev.clientX - s.startX;
      const dy = ev.clientY - s.startY;
      if (!s.active && (Math.abs(dx) > 3 || Math.abs(dy) > 3)) {
        s.active = true;
        suppressClickRef.current = true;
      }
      if (!s.active) return;

      const a = toLocal(s.startX, s.startY);
      const b = toLocal(ev.clientX, ev.clientY);
      const rect = {
        left: Math.min(a.x, b.x),
        top: Math.min(a.y, b.y),
        right: Math.max(a.x, b.x),
        bottom: Math.max(a.y, b.y),
      };
      setMarqueeRect(rect);

      const rows = Array.from(listEl.querySelectorAll('.record-row'));
      const hitIds = [];
      rows.forEach((row, i) => {
        const rr = row.getBoundingClientRect();
        const local = {
          left: rr.left - listRect.left + listEl.scrollLeft,
          top: rr.top - listRect.top + listEl.scrollTop,
          right: rr.right - listRect.left + listEl.scrollLeft,
          bottom: rr.bottom - listRect.top + listEl.scrollTop,
        };
        const intersects =
          local.left < rect.right &&
          local.right > rect.left &&
          local.top < rect.bottom &&
          local.bottom > rect.top;
        if (intersects) {
          const id = macroRecordsRef.current[i]?.id;
          if (id != null) hitIds.push(id);
        }
      });

      if (s.additive) {
        const set = new Set(s.baseSelection);
        hitIds.forEach((id) => set.add(id));
        setSelectedRecords(Array.from(set));
      } else {
        setSelectedRecords(hitIds);
      }
    };

    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      marqueeSessionRef.current = null;
      setMarqueeRect(null);
      setTimeout(() => { suppressClickRef.current = false; }, 0);
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  /**
   * 将已有的宏绑定到选中的按键（不保存宏编辑）
   */
  const bindMacroToButton = async (macroId, targetButton = selectedButton) => {
    if (!targetButton) {
      toast.warning('请先在鼠标预览图上选择要绑定的按键');
      return;
    }

    if (!isConnected || !sender) {
      toast.error('设备未连接');
      return;
    }

    const macro = macros.find(m => m.id === macroId);
    if (!macro) {
      toast.error('未找到宏');
      return;
    }

    // 检查宏是否已提交到设备
    if (!macro.fromDevice) {
      toast.warning('请先提交宏到设备后再绑定');
      return;
    }

    const buttonIndex = BUTTON_INDEX_MAP[targetButton];
    if (buttonIndex === undefined) {
      toast.error('无效的按键选择');
      return;
    }

    try {
      // 获取宏序号
      const macroIndex = macro.macroIndex ?? macros.findIndex(m => m.id === macroId);
      // 宏绑定格式：
      //   宏9 : 0x0901[序号][类型]，绑定时用默认循环设置（类型01=一次），
      //         用户可在绑定后通过预览图旁的面板修改
      //   宏12: 0x0C01[序号]00，循环模式在宏对象里
      const keyValue = encodeNewMacroBinding(protocolVersion, macroIndex);

      console.log(`绑定宏到按键: ${targetButton}(序号${buttonIndex}) -> ${keyValue}, 宏: ${macro.name}`);
      await sender.setButtonMapping(buttonIndex, keyValue);

      // 刷新按键数据
      await sender.wait(100);
      await sender.getMouseButtonData();

      toast.success(`已将"${macro.name}"绑定到${MOUSE_BUTTON_LABELS[targetButton] || targetButton}`);
    } catch (error) {
      console.error('绑定宏失败:', error);
      toast.error('绑定失败: ' + error.message);
    }
  };

  /**
   * 将宏记录转换为设备事件格式
   * 合并延时记录到前一个事件的delay字段
   */
  const convertRecordsToEvents = (records) => {
    const events = [];
    const normalizedRecords = normalizeDelayRecords(records);

    for (let i = 0; i < normalizedRecords.length; i++) {
      const record = normalizedRecords[i];

      if (record.type === 'delay') {
        // 延时记录：合并到前一个事件的delay字段
        // 鼠标移动事件没有延时字段（固定 5ms），不能合并
        const previousEvent = events[events.length - 1];
        if (previousEvent && previousEvent.type !== MACRO_EVENT_TYPE_MOUSE_MOVE) {
          previousEvent.delay += record.value || record.delay || 0;
        }
      } else if (record.type === 'move') {
        // 鼠标移动（协议 V3.0 类型8）：X/Y 写入 byte1-3，延时固定 5ms
        events.push({
          action: 'move',
          type: MACRO_EVENT_TYPE_MOUSE_MOVE,
          value: 0,
          moveX: clampMacroMoveX(record.moveX ?? 0),
          moveY: clampMacroMoveY(record.moveY ?? 0),
          delay: MACRO_MOUSE_MOVE_DELAY_MS,
        });
      } else if (record.type === 'axis' || isMacroAxisEventType(record.eventType)) {
        // 滚轮 / 鼠标X / 鼠标Y（类型 5/6/7）：有符号数值按补码写入 byte1，只有触发
        events.push({
          action: 'down',
          type: record.eventType || MACRO_EVENT_TYPE_WHEEL,
          value: encodeMacroAxisValue(record.value ?? 0),
          delay: 0
        });
      } else {
        // 非延时记录：创建事件
        let eventType = record.eventType;

        // 如果没有eventType，根据type推断
        if (!eventType) {
          switch (record.type) {
            case 'key':
              // 检查是否是修饰键 (0xE0-0xE7)
              eventType = (record.keyCode >= 0xE0 && record.keyCode <= 0xE7) ? 2 : 1;
              break;
            case 'mouse':
              eventType = MACRO_EVENT_TYPE_MOUSE_BUTTON;
              break;
            default:
              eventType = 1;
          }
        }

        const rawValue = record.value || record.keyCode || 0;

        events.push({
          action: record.action === 'up' ? 'up' : 'down',
          type: eventType,
          // 类型4 的 byte1 是按键位掩码
          value: eventType === MACRO_EVENT_TYPE_MOUSE_BUTTON
            ? clampMacroMouseButtons(rawValue)
            : rawValue,
          delay: 0
        });
      }
    }

    return events;
  };

  /**
   * 构建宏对象用于发送到设备
   * 循环设置：宏12 写进 byte5（类型）与 byte2-3（次数）；
   * 宏9 这两个字段恒为 1，循环由按键绑定的 0x09[次数][序号][类型] 决定。
   */
  const buildMacroObject = (macro, macroIndex) => {
    const events = convertRecordsToEvents(macro.records || macroRecords);

    const loopType = macroLoopInMacroObject
      ? macroLoopTypeFromMode(macro.loopMode)
      : MACRO_LOOP_TYPE_ONCE;
    // 次数只在类型4（连发N次后停止）和类型6（连发N次直到任意键）下有意义
    const loopCount = macroLoopTypeUsesCount(loopType)
      ? clampMacroLoopCount(macro.loopCount)
      : MACRO_LOOP_COUNT_MIN;

    // 延时：none=不带延时事件；dynamic=各事件自带 delay（设备 delayMode=0）
    // 设备协议 delayMode: 0=单独延时
    return {
      loopCount,   // byte2-3
      index: macroIndex,
      type: loopType,  // byte5
      name: macro.name || `宏 ${macroIndex + 1}`,
      delayMode: 0,
      events: events
    };
  };

  const importMacroToDevice = async (macroId) => {
    const localMacro = macros.find(
      (macro) => String(macro.id) === String(macroId) && !macro.fromDevice
    );
    if (!localMacro) {
      toast.error('未找到本地宏');
      return;
    }
    if (!isConnected || !sender || !dataStore) {
      toast.error('设备未连接，无法导入');
      return;
    }
    if (!localMacro.records?.length) {
      toast.warning('空宏无法导入设备');
      return;
    }

    const macroIndex = getNextMacroIndex();
    const onboardCopy = {
      ...localMacro,
      id: `device-pending-${macroIndex}-${Date.now()}`,
      macroIndex,
      fromDevice: true,
      source: 'device',
    };
    const onboardMacros = [
      ...macros.filter((macro) => macro.fromDevice),
      onboardCopy,
    ];

    setImportingMacroId(macroId);
    try {
      const macroObjects = onboardMacros.map((macro, index) =>
        buildMacroObject(macro, macro.macroIndex !== undefined ? macro.macroIndex : index)
      );
      const macroData = dataStore.buildMacroData(macroObjects);
      if (macroData.length + 4 > 4096) {
        setShowDataOverflowModal(true);
        return;
      }

      await sender.setMacroData(macroData);
      setMacros((prev) => [...prev, onboardCopy]);
      await sender.wait(200);
      await sender.getMacroData(1);
      toast.success(`“${localMacro.name}”已复制到板载宏，本地宏仍保留`);
    } catch (error) {
      console.error('导入本地宏失败:', error);
      toast.error('导入失败: ' + error.message);
    } finally {
      setImportingMacroId(null);
      setIsBoardDropActive(false);
    }
  };

  const submitMacro = async () => {
    if (!selectedMacro) {
      toast.warning('请先选择一个宏');
      return;
    }

    const name = macroDraftName.trim();
    if (!name) {
      toast.warning('请输入宏名称');
      setMacroWizardStep('name');
      return;
    }

    // 如果正在录制，先停止录制
    if (isMacroRecording) {
      setIsMacroRecording(false);
    }

    // 新建：组装对象；编辑：基于列表项
    const existing = macros.find(m => m.id === selectedMacro);
    const baseMacro = existing || {
      id: selectedMacro,
      records: [],
      delayMode: 'none',
      source: 'local',
      fromDevice: false,
    };

    const updatedMacro = {
      ...baseMacro,
      name,
      macroType: macroDraftType,
      records: normalizeDelayRecords(macroRecords),
      delayMode: delayMode,
      loopMode: macroLoopMode,
      loopCount: clampMacroLoopCount(macroLoopCount),
    };

    const updatedMacros = existing
      ? macros.map(m => (m.id === selectedMacro ? updatedMacro : m))
      : [...macros, updatedMacro];

    // 新建宏和本地宏只保存到网页，不要求设备连接。
    if (!existing?.fromDevice) {
      setMacros(updatedMacros);
      setMacroIsNew(false);
      setMacroEditorOpen(false);
      setMacroWizardStep('edit');
      toast.success('宏已保存到本地环境');
      return;
    }

    if (!isConnected || !sender || !dataStore) {
      toast.error('设备未连接');
      return;
    }

    setMacros(updatedMacros);
    setMacroIsNew(false);

    try {
      // 只向设备发送板载宏，本地宏绝不混入设备数据。
      const onboardMacros = updatedMacros.filter((macro) => macro.fromDevice);
      const macroObjects = onboardMacros.map((macro, index) =>
        buildMacroObject(macro, macro.macroIndex !== undefined ? macro.macroIndex : index)
      );

      console.log('构建宏对象:', macroObjects);

      // 使用 dataStore 构建宏数据
      const macroData = dataStore.buildMacroData(macroObjects);
      console.log('构建宏数据:', macroData.length, '字节');

      // 检查数据大小：宏数据 + 总长度(2字节，已包含) + 包头(4字节) 不能超过 4096 字节
      const totalSize = macroData.length + 4; // macroData 已包含2字节总长度，加上4字节包头
      if (totalSize > 4096) {
        setShowDataOverflowModal(true);
        return;
      }

      // 发送到设备
      await sender.setMacroData(macroData);

      // 等待设备处理
      await sender.wait(200);

      // 重新获取宏数据以验证
      await sender.getMacroData(1);

      toast.success('板载宏已保存');
      setMacroEditorOpen(false);
      setMacroWizardStep('edit');
      setMacroIsNew(false);
    } catch (error) {
      console.error('保存宏数据失败:', error);
      toast.error('保存失败: ' + error.message);
    }
  };

  // 宏录制键盘监听
  // 宏录制 - 监听键盘和鼠标事件
  useEffect(() => {
    if (!isMacroRecording) return;

    const jsKeyToHID = JS_KEY_TO_HID;
    const modifierNames = HID_MODIFIER_NAMES;

    // 鼠标按键映射: button -> { value, name }
    // value 为协议类型4 的位掩码（不是按键序号）
    const mouseButtonMap = {
      0: { value: MACRO_MOUSE_BUTTON_LEFT, name: '左键' },
      1: { value: MACRO_MOUSE_BUTTON_MIDDLE, name: '中键' },
      2: { value: MACRO_MOUSE_BUTTON_RIGHT, name: '右键' },
      3: { value: MACRO_MOUSE_BUTTON_BACK, name: '侧后键' },
      4: { value: MACRO_MOUSE_BUTTON_FORWARD, name: '侧前键' },
    };

    let lastEventTime = Date.now();
    const pressedKeys = new Set(); // 跟踪已按下的键，防止重复
    const pressedMouseButtons = new Set(); // 跟踪已按下的鼠标键

    // 创建延时记录
    const createDelayRecord = (delay) => {
      return {
        id: Date.now() + Math.random(),
        type: 'delay',
        eventType: 0, // 0=延时
        keyCode: 0,
        keyName: `${delay}ms`,
        value: delay,
        modifiers: [],
        delay: delay,
        action: 'delay'
      };
    };

    // 计算延时并创建延时记录（如果需要）
    const getDelayRecords = () => {
      const now = Date.now();
      const delay = delayMode === 'dynamic' ? now - lastEventTime : 0;
      lastEventTime = now;

      if (macroRecordsRef.current.length === 0) return [];
      if (delay > 0 && delayMode === 'dynamic') {
        return [createDelayRecord(delay)];
      }
      return [];
    };

    // 创建键盘记录
    const createKeyRecord = (hidCode, action) => {
      const isModifier = hidCode >= 0xE0 && hidCode <= 0xE7;
      let keyName;

      if (isModifier) {
        keyName = modifierNames[hidCode] || `Mod 0x${hidCode.toString(16)}`;
      } else {
        const keyInfo = KEY_CODES.find(k => k.code === hidCode);
        keyName = keyInfo ? keyInfo.label.replace('\n', '') : `0x${hidCode.toString(16)}`;
      }

      return {
        id: Date.now() + Math.random(),
        type: 'key',
        eventType: isModifier ? 2 : 1, // 2=修饰键, 1=普通键
        keyCode: hidCode,
        keyName: keyName,
        value: hidCode,
        modifiers: [],
        delay: 0,
        action: action
      };
    };

    // 创建鼠标按键记录（类型4，byte1 为位掩码）
    const createMouseRecord = (buttonMask, action) =>
      createMouseButtonRecord(Date.now() + Math.random(), buttonMask, action);

    // 创建滚轮记录（类型5，byte1 为有符号步数：上为正，下为负）
    const createScrollRecord = (direction) =>
      createAxisRecord(
        Date.now() + Math.random(),
        MACRO_EVENT_TYPE_WHEEL,
        direction === 'up' ? 1 : -1
      );

    // 键盘按下
    const handleKeyDown = (e) => {
      e.preventDefault();
      const hidCode = jsKeyToHID[e.code];
      console.log('[宏监听] 键盘按下', {
        key: e.key,
        code: e.code,
        value: hidCode ?? null,
      });
      if (!hidCode) return;
      if (pressedKeys.has(e.code)) return;
      pressedKeys.add(e.code);
      const delayRecords = getDelayRecords();
      setMacroRecords(prev => [...prev, ...delayRecords, createKeyRecord(hidCode, 'down')]);
    };

    // 键盘抬起
    const handleKeyUp = (e) => {
      e.preventDefault();
      const hidCode = jsKeyToHID[e.code];
      console.log('[宏监听] 键盘抬起', {
        key: e.key,
        code: e.code,
        value: hidCode ?? null,
      });
      if (!hidCode) return;
      pressedKeys.delete(e.code);
      const delayRecords = getDelayRecords();
      setMacroRecords(prev => [...prev, ...delayRecords, createKeyRecord(hidCode, 'up')]);
    };

    // 检查是否点击在指定元素附近
    const isNearElement = (e, ref) => {
      if (!ref.current) return false;
      const rect = ref.current.getBoundingClientRect();
      const padding = 10; // 扩大检测范围
      return (
        e.clientX >= rect.left - padding &&
        e.clientX <= rect.right + padding &&
        e.clientY >= rect.top - padding &&
        e.clientY <= rect.bottom + padding
      );
    };

    // 检查是否点击在录制按钮或提交按钮附近
    const isNearExcludedButtons = (e) => {
      return isNearElement(e, recordButtonRef) || isNearElement(e, submitButtonRef);
    };

    // 鼠标按下
    const handleMouseDown = (e) => {
      const buttonInfo = mouseButtonMap[e.button];
      console.log('[宏监听] 鼠标按下', {
        button: e.button,
        value: buttonInfo?.value ?? null,
      });

      // 如果点击在录制按钮或提交按钮附近，不记录左键
      if (e.button === 0 && isNearExcludedButtons(e)) return;

      if (!buttonInfo) return;
      if (pressedMouseButtons.has(e.button)) return;
      pressedMouseButtons.add(e.button);
      const delayRecords = getDelayRecords();
      setMacroRecords(prev => [...prev, ...delayRecords, createMouseRecord(buttonInfo.value, 'down')]);
    };

    // 鼠标抬起
    const handleMouseUp = (e) => {
      const buttonInfo = mouseButtonMap[e.button];
      console.log('[宏监听] 鼠标抬起', {
        button: e.button,
        value: buttonInfo?.value ?? null,
      });

      // 如果点击在录制按钮或提交按钮附近，不记录左键
      if (e.button === 0 && isNearExcludedButtons(e)) return;

      if (!buttonInfo) return;
      pressedMouseButtons.delete(e.button);
      const delayRecords = getDelayRecords();
      setMacroRecords(prev => [...prev, ...delayRecords, createMouseRecord(buttonInfo.value, 'up')]);
    };

    // 滚轮滚动
    const handleWheel = (e) => {
      e.preventDefault();
      const direction = e.deltaY < 0 ? 'up' : 'down';
      console.log('[宏监听] 滚轮', {
        deltaY: e.deltaY,
        direction,
        value: direction === 'up' ? 1 : -1,
      });
      const delayRecords = getDelayRecords();
      setMacroRecords(prev => [...prev, ...delayRecords, createScrollRecord(direction)]);
    };

    // 阻止右键菜单
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isMacroRecording, delayMode]);

  // 宏字符面板按键
  // label: 按钮展示（数字/符号键可双行显示 Shift 字符）；赋值名称一律按 HID 解析，不写死展示文案
  const macroBasicKeys = [
    // Esc
    { label: 'Esc', code: 0x29 },
    // 字母键 A-Z
    { label: 'A', code: 0x04 }, { label: 'B', code: 0x05 }, { label: 'C', code: 0x06 },
    { label: 'D', code: 0x07 }, { label: 'E', code: 0x08 }, { label: 'F', code: 0x09 },
    { label: 'G', code: 0x0A }, { label: 'H', code: 0x0B }, { label: 'I', code: 0x0C },
    { label: 'J', code: 0x0D }, { label: 'K', code: 0x0E }, { label: 'L', code: 0x0F },
    { label: 'M', code: 0x10 }, { label: 'N', code: 0x11 }, { label: 'O', code: 0x12 },
    { label: 'P', code: 0x13 }, { label: 'Q', code: 0x14 }, { label: 'R', code: 0x15 },
    { label: 'S', code: 0x16 }, { label: 'T', code: 0x17 }, { label: 'U', code: 0x18 },
    { label: 'V', code: 0x19 }, { label: 'W', code: 0x1A }, { label: 'X', code: 0x1B },
    { label: 'Y', code: 0x1C }, { label: 'Z', code: 0x1D },
    // 数字键（上行为 Shift 字符，下行为实际键位；点选写入下层键名）
    { label: '!\n1', code: 0x1E }, { label: '@\n2', code: 0x1F }, { label: '#\n3', code: 0x20 },
    { label: '$\n4', code: 0x21 }, { label: '%\n5', code: 0x22 }, { label: '^\n6', code: 0x23 },
    { label: '&\n7', code: 0x24 }, { label: '*\n8', code: 0x25 }, { label: '(\n9', code: 0x26 },
    { label: ')\n0', code: 0x27 },
    // 符号键
    { label: '_\n-', code: 0x2D }, { label: '+\n=', code: 0x2E }, { label: '~\n`', code: 0x35 },
    { label: '{\n[', code: 0x2F }, { label: '}\n]', code: 0x30 }, { label: '|\n\\', code: 0x31 },
    { label: ':\n;', code: 0x33 }, { label: '"\n\'', code: 0x34 },
    { label: '<\n,', code: 0x36 }, { label: '>\n.', code: 0x37 }, { label: '?\n/', code: 0x38 },
    // 小键盘 = 和 ,
    { label: 'N=', code: 0x67 }, { label: 'N,', code: 0x85 },
    // 功能键 F1-F12
    { label: 'F1', code: 0x3A }, { label: 'F2', code: 0x3B }, { label: 'F3', code: 0x3C },
    { label: 'F4', code: 0x3D }, { label: 'F5', code: 0x3E }, { label: 'F6', code: 0x3F },
    { label: 'F7', code: 0x40 }, { label: 'F8', code: 0x41 }, { label: 'F9', code: 0x42 },
    { label: 'F10', code: 0x43 }, { label: 'F11', code: 0x44 }, { label: 'F12', code: 0x45 },
    // 特殊键
    { label: 'PrtSc', code: 0x46 }, { label: 'Scroll', code: 0x47 }, { label: 'Pause', code: 0x48 },
    { label: 'Tab', code: 0x2B }, { label: 'Bksp', code: 0x2A },
    { label: 'Insert', code: 0x49 }, { label: 'Del', code: 0x4C },
    { label: 'Home', code: 0x4A }, { label: 'End', code: 0x4D },
    { label: 'PgUp', code: 0x4B }, { label: 'PgDn', code: 0x4E },
    { label: 'NumLk', code: 0x53 }, { label: 'CapsLk', code: 0x39 }, { label: 'Enter', code: 0x28 },
    // 小键盘数字
    { label: 'N1', code: 0x59 }, { label: 'N2', code: 0x5A }, { label: 'N3', code: 0x5B },
    { label: 'N4', code: 0x5C }, { label: 'N5', code: 0x5D }, { label: 'N6', code: 0x5E },
    { label: 'N7', code: 0x5F }, { label: 'N8', code: 0x60 }, { label: 'N9', code: 0x61 },
    { label: 'N0', code: 0x62 },
    // 小键盘符号
    { label: 'N/', code: 0x54 }, { label: 'N*', code: 0x55 },
    { label: 'N-', code: 0x56 }, { label: 'N+', code: 0x57 },
    { label: 'N.', code: 0x63 }, { label: 'NEnt', code: 0x58 },
    // 修饰键
    { label: 'LShift', code: 0xE1 }, { label: 'RShift', code: 0xE5 },
    { label: 'L Ctrl', code: 0xE0 }, { label: 'R Ctrl', code: 0xE4 },
    { label: 'L Win', code: 0xE3 }, { label: 'R Win', code: 0xE7 },
    { label: 'L Alt', code: 0xE2 }, { label: 'R Alt', code: 0xE6 },
    // 空格和菜单
    { label: 'Space', code: 0x2C }, { label: 'Menu', code: 0x65 },
    // 方向键
    { label: 'Left', code: 0x50 }, { label: 'Down', code: 0x51 },
    { label: 'Up', code: 0x52 }, { label: 'Right', code: 0x4F },
  ];

  // 字符面板点击：用 HID 名作为记录名（@\\n2 → 2），避免把展示文案整串写入
  const resolveCharPanelKeyName = (key) => {
    const hidName = resolveHidKeyName(key.code);
    // KEY_CODES / 修饰键能解析出正常名称
    if (hidName && !/^0x/i.test(hidName)) return hidName;
    // 双行标签取下层（未按 Shift 的键位字符）
    if (key.label && key.label.includes('\n')) {
      const parts = key.label.split('\n');
      return parts[parts.length - 1] || key.label;
    }
    return key.label || hidName;
  };

  // 渲染宏界面：列表只显示已保存；新建走 命名 → 类型 → 编辑
  const renderMacroGrid = () => {
    const onboardMacros = macros.filter((macro) => macro.fromDevice);
    const localMacros = macros.filter((macro) => !macro.fromDevice);
    const editingMacro = macros.find((m) => m.id === selectedMacro);
    const workspaceMacro =
      macroWorkspace && !macroIsNew ? editingMacro || macros[0] : editingMacro;
    const shouldShowMacroEditor =
      (macroEditorOpen && (macroIsNew || editingMacro)) ||
      (isWorkspaceEditorVisible && workspaceMacro);
    const editorTitle =
      macroWizardStep === 'name'
        ? '新建宏'
        : '编辑宏';
    const editorSub =
      macroWizardStep === 'edit'
        ? (macroDraftName || workspaceMacro?.name || '')
        : '步骤 1 / 2 · 命名';

    const renderMacroItem = (macro, source) => {
      const isLocal = source === 'local';
      const isDragging = isLocal
        ? draggedLocalMacroId === macro.id
        : assignDrag?.kind === 'macro' && assignDrag.macroId === macro.id;
      return (
        <div
          key={macro.id}
          className={[
            'macro-item',
            selectedMacro === macro.id && macroEditorOpen ? 'active' : '',
            isLocal ? 'local-environment' : 'from-device',
            isDragging ? 'is-dragging' : '',
          ].filter(Boolean).join(' ')}
          draggable
          onDragStart={(event) => {
            if (isLocal) setDraggedLocalMacroId(macro.id);
            beginAssignDrag(
              event,
              {
                kind: 'macro',
                macroId: macro.id,
                onboard: !isLocal,
                label: macro.name,
              },
              String(macro.id)
            );
          }}
          onDragEnd={endAssignDrag}
        >
          <span
            className="macro-drag-grip"
            title={isLocal ? '拖到板载宏可复制到设备' : '拖到鼠标按键上可绑定'}
          >
            ⋮⋮
          </span>
          <button
            type="button"
            className="macro-item-open"
            onClick={() => openMacroEditor(macro.id)}
            title={`编辑 ${macro.name}`}
          >
            <span className="macro-name">{macro.name}</span>
            <span className="macro-item-summary">
              {macro.records?.length || 0} 事件
            </span>
          </button>
          <div className="macro-item-actions">
            <button
              type="button"
              className="macro-action-btn"
              data-action="edit"
              onClick={() => openMacroEditor(macro.id)}
            >
              编辑
            </button>
            <button
              type="button"
              className={`macro-item-more ${macroListMenu?.macroId === macro.id ? 'active' : ''}`}
              data-action="more"
              aria-label={`更多操作 ${macro.name}`}
              title="更多"
              onClick={(event) => openMacroListMenu(event, macro.id, source)}
            >
              ⋯
            </button>
          </div>
        </div>
      );
    };

    return (
      <>
        <div className="macro-container macro-list-only">
          <div className="macro-list-header">
            <div className="macro-description">
              拖动本地宏到「板载宏」即可复制，原项仍会保留；板载宏可直接拖到鼠标按键上绑定。
            </div>
            <button type="button" className="macro-create-btn" onClick={addNewMacro}>
              + 新建本地宏
            </button>
          </div>

          <div className="macro-library">
            <section
              className={`macro-library-section cmd-category onboard ${boardMacrosOpen ? 'is-open' : ''} ${isBoardDropActive ? 'drop-active' : ''}`}
              onDragOver={(event) => {
                if (!draggedLocalMacroId) return;
                event.preventDefault();
                event.dataTransfer.dropEffect = 'copy';
                setIsBoardDropActive(true);
                setBoardMacrosOpen(true);
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget)) {
                  setIsBoardDropActive(false);
                }
              }}
              onDrop={(event) => {
                event.preventDefault();
                const macroId = draggedLocalMacroId || event.dataTransfer.getData('text/plain');
                setIsBoardDropActive(false);
                setDraggedLocalMacroId(null);
                if (macroId) importMacroToDevice(macroId);
              }}
            >
              <button
                type="button"
                className="macro-library-heading cmd-category-toggle"
                aria-expanded={boardMacrosOpen}
                onClick={() => setBoardMacrosOpen((open) => !open)}
              >
                <span className={`macro-library-chevron cmd-chevron ${boardMacrosOpen ? 'open' : ''}`}>›</span>
                <span className="cmd-category-label">板载宏</span>
                <span className="macro-library-count cmd-category-count">{onboardMacros.length}</span>
              </button>
              {boardMacrosOpen && <div className="macro-library-items cmd-list">
                {onboardMacros.length > 0
                  ? onboardMacros.map((macro) => renderMacroItem(macro, 'device'))
                  : <div className="macro-empty">设备中暂无宏</div>}
                {isBoardDropActive && (
                  <div className="macro-drop-copy-hint">释放以复制到设备 · 本地原项将保留</div>
                )}
              </div>}
            </section>

            <section className={`macro-library-section cmd-category local ${localMacrosOpen ? 'is-open' : ''}`}>
              <button
                type="button"
                className="macro-library-heading cmd-category-toggle"
                aria-expanded={localMacrosOpen}
                onClick={() => setLocalMacrosOpen((open) => !open)}
              >
                <span className={`macro-library-chevron cmd-chevron ${localMacrosOpen ? 'open' : ''}`}>›</span>
                <span className="cmd-category-label">本地宏</span>
                <span className="macro-library-count cmd-category-count">{localMacros.length}</span>
              </button>
              {localMacrosOpen && <div className="macro-library-items cmd-list">
                {localMacros.length > 0
                  ? localMacros.map((macro) => renderMacroItem(macro, 'local'))
                  : (
                    <button type="button" className="macro-empty-create" onClick={addNewMacro}>
                      + 创建本地宏
                    </button>
                  )}
              </div>}
            </section>
          </div>
        </div>

        {macroListMenu && renderFloatingMenu((() => {
          const menuMacro = macros.find(
            (item) => String(item.id) === String(macroListMenu.macroId)
          );
          const menuIsLocal = macroListMenu.source === 'local';
          if (!menuMacro) return null;
          const runMenuAction = (action) => {
            setMacroListMenu(null);
            action();
          };
          return (
            <div
              ref={macroListMenuRef}
              className="context-menu macro-list-menu"
              style={
                macroListMenuPos
                  ? {
                      left: macroListMenuPos.left,
                      top: macroListMenuPos.top,
                      maxHeight: macroListMenuPos.maxHeight,
                    }
                  : { visibility: 'hidden', left: macroListMenu.x, top: macroListMenu.y }
              }
              onClick={(event) => event.stopPropagation()}
            >
              {macroLoopInMacroObject && (
                <div
                  className="context-menu-item"
                  data-action="loop"
                  onClick={() => runMenuAction(() => openLoopManager(menuMacro.id))}
                >
                  <span>循环</span>
                </div>
              )}
              {menuIsLocal ? (
                <div
                  className={`context-menu-item primary ${importingMacroId === menuMacro.id ? 'is-disabled' : ''}`}
                  data-action="import"
                  onClick={() => {
                    if (importingMacroId === menuMacro.id) return;
                    runMenuAction(() => importMacroToDevice(menuMacro.id));
                  }}
                >
                  <span>{importingMacroId === menuMacro.id ? '导入中…' : '导入设备'}</span>
                </div>
              ) : (
                <div
                  className="context-menu-item primary"
                  data-action="bind"
                  onClick={() => runMenuAction(() => bindMacroToButton(menuMacro.id))}
                >
                  <span>绑定</span>
                </div>
              )}
              <div
                className="context-menu-item"
                data-action="copy"
                onClick={() => runMenuAction(() => cloneMacro(menuMacro.id))}
              >
                <span>复制</span>
              </div>
              <div
                className="context-menu-item"
                data-action="rename"
                onClick={() => runMenuAction(() => startRenameMacro(menuMacro.id, menuMacro.name))}
              >
                <span>重命名</span>
              </div>
              <div
                className="context-menu-item danger"
                data-action="delete"
                onClick={() => runMenuAction(() => deleteMacro(menuMacro.id))}
              >
                <span>删除</span>
              </div>
            </div>
          );
        })())}

        {shouldShowMacroEditor && renderMacroEditorLayer(
          <div className="modal-overlay macro-editor-overlay" onClick={closeMacroEditor}>
            <div
              className={`modal-dialog macro-editor-dialog ${macroWizardStep !== 'edit' ? 'wizard' : ''}`}
              onMouseDownCapture={(e) => {
                if (
                  showAddEventMenu &&
                  !e.target.closest('.macro-add-record-wrapper')
                ) {
                  setShowAddEventMenu(false);
                }
                if (contextMenu && !e.target.closest('.context-menu')) {
                  setContextMenu(null);
                }
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header macro-editor-header">
                <div className="macro-editor-title-wrap">
                  <span className="modal-title">{editorTitle}</span>
                  <span className="macro-editor-sub">{editorSub}</span>
                </div>
                <button type="button" className="macro-editor-close" onClick={closeMacroEditor} aria-label="关闭">×</button>
              </div>

              {/* 步骤 1：命名 */}
              {macroWizardStep === 'name' && (
                <>
                  <div className="macro-wizard-body">
                    <label className="macro-wizard-label">宏名称</label>
                    <input
                      type="text"
                      className="modal-input macro-wizard-input"
                      value={macroDraftName}
                      onChange={(e) => setMacroDraftName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          confirmMacroNameStep();
                        }
                      }}
                      placeholder="请输入宏名称"
                      autoFocus
                      maxLength={32}
                    />
                    <p className="macro-wizard-hint">输入名称后直接进入宏编辑。</p>
                  </div>
                  <div className="modal-footer macro-editor-footer">
                    <button type="button" className="modal-btn modal-btn-cancel" onClick={closeMacroEditor}>取消</button>
                    <button type="button" className="modal-btn modal-btn-submit" onClick={confirmMacroNameStep}>开始编辑</button>
                  </div>
                </>
              )}

              {/* 步骤 2：宏信息（录制 / 记录 / 字符） */}
              {macroWizardStep === 'edit' && (
                <>
              <div className="macro-editor-body">
        {/* 录制设置 */}
        <div className="macro-panel macro-middle">
          <div className="section-title">{macroDraftType === 'manual' ? '编辑' : '录制'}</div>

          {macroDraftType !== 'manual' && (
          <button
            ref={recordButtonRef}
            className={`macro-record-btn ${isMacroRecording ? 'recording' : ''}`}
            onClick={toggleMacroRecording}
          >
            <span className="record-dot"></span>
            {isMacroRecording ? '停止录制' : '开始录制'}
          </button>
          )}

          <div className="macro-settings">
            <div className="setting-group">
              <div className="setting-title">延时设置</div>
              <label className="radio-item">
                <input type="radio" checked={delayMode === 'dynamic'} onChange={() => setDelayMode('dynamic')} />
                <span>记录延迟</span>
              </label>
              <label className="radio-item">
                <input type="radio" checked={delayMode === 'none'} onChange={() => setDelayMode('none')} />
                <span>不记录延迟</span>
              </label>
            </div>
            {macroLoopInMacroObject && (
              <div className="setting-group macro-editor-loop-group">
                <div className="setting-title">循环设置</div>
                {renderLoopModeOptions({
                  mode: macroLoopMode,
                  count: macroLoopCount,
                  onModeChange: setMacroLoopMode,
                  onCountChange: setMacroLoopCount,
                  countClassName: 'macro-editor-loop-count-input',
                  burstCountClassName: 'macro-editor-loop-burst-count-input',
                })}
              </div>
            )}
          </div>
        </div>

        {/* 宏记录 */}
        <div className="macro-panel macro-records">
          <MacroRecordsHeader
            onSelectAll={selectAllRecords}
            onDelete={deleteMacroRecords}
            deleteDisabled={selectedRecords.length === 0}
          />

          <MacroRecordsList
            className={isDraggingRecords ? 'is-dragging' : ''}
            ref={recordsListRef}
            onMouseDown={handleRecordsListMouseDown}
          >
            {macroRecords.map((record, index) => (
              <React.Fragment key={record.id}>
                {dragOverIndex === index && isDraggingRecords && (
                  <div className="record-drop-indicator" />
                )}
                <div
                  className={[
                    'record-row',
                    record.type === 'delay' ? 'record-row-delay' : '',
                    record.type === 'move' ? 'record-row-move' : '',
                    record.type === 'axis' ? 'record-row-axis' : '',
                    editingRecordId === record.id ? 'editing' : '',
                    selectedRecords.includes(record.id) ? 'selected' : '',
                    (selectedKeyIds.includes(record.id) || editingRecordId === record.id) && record.type === 'key'
                      ? 'key-input-active'
                      : '',
                    isDraggingRecords && selectedRecords.includes(record.id) ? 'is-drag-source' : '',
                  ].filter(Boolean).join(' ')}
                  onMouseDown={(e) => handleRecordMouseDown(e, record, index)}
                  onClick={(e) => handleRecordClick(e, record, index)}
                  onContextMenu={(e) => {
                    e.preventDefault();
                    if (!selectedRecords.includes(record.id)) {
                      setSelectedRecords([record.id]);
                      selectionAnchorRef.current = index;
                      lastSelectedIndexRef.current = index;
                    }
                    openContextMenu(e, record.id);
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedRecords.includes(record.id)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleRecordSelect(record.id, index, {
                        ...e,
                        metaKey: true,
                        ctrlKey: true,
                        shiftKey: e.nativeEvent?.shiftKey || e.shiftKey,
                      });
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    className="record-checkbox"
                  />
                  <span className="record-drag-handle" title="拖动排序" aria-hidden>⋮⋮</span>
                  <span className="record-index">{index + 1}</span>
                  {record.type === 'delay' ? (
                    <>
                      <span className="record-action action-delay">⏱</span>
                      <div
                        className={`record-delay-input ${selectedDelayIds.includes(record.id) ? 'delay-input-selected' : ''}`}
                        onMouseDown={(e) => {
                          // 在容器上 Ctrl/Shift 点选，不抢输入焦点逻辑
                          if (e.metaKey || e.ctrlKey || e.shiftKey) {
                            e.preventDefault();
                            handleDelayInputSelect(record.id, e);
                          }
                          e.stopPropagation();
                        }}
                      >
                        <span className="delay-label">延时</span>
                        <span
                          className={`delay-related-event ${getDelayOwnerLabel(index) === '未关联事件' ? 'missing' : ''}`}
                          title={`关联事件：${getDelayOwnerLabel(index)}`}
                        >
                          关联：{getDelayOwnerLabel(index)}
                        </span>
                        <input
                          type="number"
                          className={`delay-input-field ${selectedDelayIds.includes(record.id) ? 'delay-field-selected' : ''}`}
                          value={record.delay || 0}
                          onChange={(e) => updateDelayValue(record.id, e.target.value)}
                          onMouseDown={(e) => {
                            e.stopPropagation();
                            // Ctrl/Shift：只改延时多选，阻止抢焦点以免 onFocus 覆盖
                            if (e.metaKey || e.ctrlKey || e.shiftKey) {
                              e.preventDefault();
                            }
                            handleDelayInputSelect(record.id, e);
                          }}
                          onClick={(e) => e.stopPropagation()}
                          onFocus={() => {
                            clearKeyInputSelection();
                            // 无修饰键聚焦：若未在多选组内则独占
                            const cur = selectedDelayIdsRef.current;
                            if (!(cur.length > 1 && cur.includes(record.id))) {
                              const delays = getDelaySequence();
                              const di = delays.findIndex((d) => d.id === record.id);
                              setSelectedDelayIds([record.id]);
                              delaySelectAnchorRef.current = di;
                            }
                          }}
                          min="0"
                          max="65535"
                          title={
                            selectedDelayIds.length > 1 && selectedDelayIds.includes(record.id)
                              ? `将同步修改 ${selectedDelayIds.length} 条延时`
                              : 'Ctrl/⌘ 或 Shift 点击可多选延时'
                          }
                        />
                        <span className="delay-unit">ms</span>
                      </div>
                    </>
                  ) : record.type === 'move' ? (
                    <>
                      <span
                        className="record-action action-move"
                        title={`鼠标移动 · 固定 ${MACRO_MOUSE_MOVE_DELAY_MS}ms 延时`}
                      >
                        ✥
                      </span>
                      <div
                        className="record-move-input"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="move-label">移动</span>
                        <span className="move-axis">X</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="move-input-field"
                          value={getMoveFieldValue(record, 'x')}
                          onChange={(e) => handleMoveInputChange(record.id, 'x', e.target.value)}
                          onBlur={() => commitMoveInput(record.id, 'x')}
                          title={`X 范围 ${MACRO_MOUSE_MOVE_X_MIN} ~ ${MACRO_MOUSE_MOVE_X_MAX}`}
                        />
                        <span className="move-axis">Y</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="move-input-field"
                          value={getMoveFieldValue(record, 'y')}
                          onChange={(e) => handleMoveInputChange(record.id, 'y', e.target.value)}
                          onBlur={() => commitMoveInput(record.id, 'y')}
                          title={`Y 范围 ${MACRO_MOUSE_MOVE_Y_MIN} ~ ${MACRO_MOUSE_MOVE_Y_MAX}`}
                        />
                        <span className="move-fixed-delay">{MACRO_MOUSE_MOVE_DELAY_MS}ms</span>
                      </div>
                    </>
                  ) : record.type === 'axis' ? (
                    <>
                      <span
                        className="record-action action-axis"
                        title={`${MACRO_EVENT_TYPE_NAMES[record.eventType]} · 只有触发，没有抬起`}
                      >
                        ⇅
                      </span>
                      <div
                        className="record-axis-input"
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <span className="axis-label">{MACRO_EVENT_TYPE_NAMES[record.eventType]}</span>
                        <input
                          type="text"
                          inputMode="numeric"
                          className="axis-input-field"
                          value={getMoveFieldValue(record, 'v')}
                          onChange={(e) => handleMoveInputChange(record.id, 'v', e.target.value)}
                          onBlur={() => commitMoveInput(record.id, 'v')}
                          title={`取值范围 ${MACRO_AXIS_MIN} ~ ${MACRO_AXIS_MAX}`}
                        />
                        <span className="axis-range">{MACRO_AXIS_MIN} ~ {MACRO_AXIS_MAX}</span>
                      </div>
                    </>
                  ) : record.type === 'mouse' ? (
                    <>
                      <span
                        className={`record-action ${record.action === 'up' ? 'action-up' : 'action-down'}`}
                        style={{ cursor: 'pointer' }}
                        title="点击切换按下/抬起"
                        onClick={(e) => {
                          e.stopPropagation();
                          const newRecords = macroRecords.map(r =>
                            r.id === record.id ? { ...r, action: r.action === 'up' ? 'down' : 'up' } : r
                          );
                          setMacroRecords(newRecords);
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {record.action === 'up' ? '↑' : '↓'}
                      </span>
                      <select
                        className="record-select"
                        value={getMouseButtonMask(record)}
                        onChange={(e) => {
                          const mask = clampMacroMouseButtons(e.target.value);
                          setMacroRecords((prev) => prev.map(r =>
                            r.id === record.id
                              ? {
                                  ...r,
                                  valueScheme: 'mask',
                                  keyCode: mask,
                                  value: mask,
                                  keyName: formatMacroMouseButtons(mask),
                                }
                              : r
                          ));
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {MACRO_MOUSE_BUTTON_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        {/* 设备上的宏可能一个事件按下多个键，位掩码不是单键时保留原值 */}
                        {!MACRO_MOUSE_BUTTON_OPTIONS.some(
                          (option) => option.value === getMouseButtonMask(record)
                        ) && (
                          <option value={getMouseButtonMask(record)}>
                            {formatMacroMouseButtons(getMouseButtonMask(record))}
                          </option>
                        )}
                      </select>
                    </>
                  ) : (
                    <>
                      <span
                        className={`record-action ${record.action === 'up' ? 'action-up' : 'action-down'}`}
                        style={{ cursor: 'pointer' }}
                        title="点击切换按下/抬起"
                        onClick={(e) => {
                          e.stopPropagation();
                          const selected = selectedKeyIdsRef.current;
                          const multi =
                            selected.length > 1 && selected.includes(record.id);
                          const targets = multi ? new Set(selected) : new Set([record.id]);
                          setMacroRecords((prev) =>
                            prev.map((r) =>
                              r.type === 'key' && targets.has(r.id)
                                ? { ...r, action: r.action === 'up' ? 'down' : 'up' }
                                : r
                            )
                          );
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        {record.action === 'up' ? '↑' : '↓'}
                      </span>
                      <input
                        type="text"
                        className={[
                          'record-input',
                          selectedKeyIds.includes(record.id) || editingRecordId === record.id
                            ? 'key-field-selected'
                            : '',
                          editingRecordId === record.id || selectedKeyIds.includes(record.id)
                            ? 'listening-key'
                            : '',
                        ].filter(Boolean).join(' ')}
                        value={record.keyName || ''}
                        placeholder={
                          editingRecordId === record.id || selectedKeyIds.includes(record.id)
                            ? '按下键盘按键…'
                            : '请选择/按下按键'
                        }
                        readOnly
                        onMouseDown={(e) => {
                          e.stopPropagation();
                          if (e.metaKey || e.ctrlKey || e.shiftKey) {
                            e.preventDefault();
                          }
                          handleKeyInputSelect(record.id, e);
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          // 无修饰键 click：进入编辑（多选组内保持组）
                          if (!e.metaKey && !e.ctrlKey && !e.shiftKey) {
                            setEditingRecordId(record.id);
                            // 保证选中目标可用于键盘赋值
                            if (!selectedKeyIdsRef.current.includes(record.id)) {
                              setSelectedKeyIds([record.id]);
                              const seq = getKeySequence();
                              keySelectAnchorRef.current = seq.findIndex((d) => d.id === record.id);
                            }
                          }
                        }}
                        title={
                          selectedKeyIds.length > 1 && selectedKeyIds.includes(record.id)
                            ? `将同步修改 ${selectedKeyIds.length} 条按键 · 可直接按键赋值`
                            : '点击后按键盘赋值 · Ctrl/⌘ 或 Shift 多选'
                        }
                      />
                    </>
                  )}
                  <div className="record-controls">
                    <button
                      className={`control-btn ${contextMenu?.recordId === record.id ? 'active' : ''}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        openContextMenu(e, record.id);
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >⋯</button>
                  </div>
                </div>
              </React.Fragment>
            ))}
            {dragOverIndex === macroRecords.length && isDraggingRecords && (
              <div className="record-drop-indicator" />
            )}
            {marqueeRect && (
              <div
                className="record-marquee"
                style={{
                  left: marqueeRect.left,
                  top: marqueeRect.top,
                  width: Math.max(0, marqueeRect.right - marqueeRect.left),
                  height: Math.max(0, marqueeRect.bottom - marqueeRect.top),
                }}
              />
            )}
          </MacroRecordsList>

          {/* 三点菜单挂到应用根层，避免主题1工作区/对话框 overflow 把菜单裁掉 */}
          {contextMenu && renderFloatingMenu(
            <div
              ref={contextMenuRef}
              className="context-menu"
              style={
                contextMenuPos
                  ? {
                      left: contextMenuPos.left,
                      top: contextMenuPos.top,
                      maxHeight: contextMenuPos.maxHeight,
                    }
                  : { visibility: 'hidden', left: contextMenu.x, top: contextMenu.y }
              }
              onClick={(e) => e.stopPropagation()}
            >
              <div
                className={`context-menu-item has-submenu ${contextMenu.submenu === 'above' ? 'active' : ''}`}
                onMouseEnter={() => setContextMenu({...contextMenu, submenu: 'above'})}
              >
                <span>向上插入</span>
                <span className="submenu-arrow">›</span>
                {contextMenu.submenu === 'above' && (
                  <div
                    ref={contextSubmenuRef}
                    className="context-submenu"
                    style={contextSubmenuPos || { visibility: 'hidden' }}
                  >
                    {macroEventMenuGroups.map((group) => (
                      <div className="menu-group" key={group.title}>
                        <div className="menu-group-title">{group.title}</div>
                        {group.items.map((item) => (
                          <div
                            key={item.key}
                            className="context-menu-item"
                            onClick={() => insertRecord(contextMenu.recordId, 'above', item.key)}
                          >
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className={`context-menu-item has-submenu ${contextMenu.submenu === 'below' ? 'active' : ''}`}
                onMouseEnter={() => setContextMenu({...contextMenu, submenu: 'below'})}
              >
                <span>向下插入</span>
                <span className="submenu-arrow">›</span>
                {contextMenu.submenu === 'below' && (
                  <div
                    ref={contextSubmenuRef}
                    className="context-submenu"
                    style={contextSubmenuPos || { visibility: 'hidden' }}
                  >
                    {macroEventMenuGroups.map((group) => (
                      <div className="menu-group" key={group.title}>
                        <div className="menu-group-title">{group.title}</div>
                        {group.items.map((item) => (
                          <div
                            key={item.key}
                            className="context-menu-item"
                            onClick={() => insertRecord(contextMenu.recordId, 'below', item.key)}
                          >
                            <span>{item.label}</span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div
                className="context-menu-item"
                onMouseEnter={() => setContextMenu({...contextMenu, submenu: null})}
                onClick={() => deleteRecord(contextMenu.recordId)}
              >
                <span>删除</span>
              </div>
            </div>
          )}

          <div className="macro-add-record-wrapper">
            <button
              ref={addEventButtonRef}
              className="macro-add-record-btn"
              onClick={() => setShowAddEventMenu(!showAddEventMenu)}
            >
              <span>+</span>
            </button>
            {showAddEventMenu && (
              <div
                ref={addEventMenuRef}
                className="add-event-menu"
                // 定位前先隐藏，避免量高那一帧闪在错误位置
                style={
                  addEventMenuPos
                    ? {
                        left: addEventMenuPos.left,
                        top: addEventMenuPos.top,
                        width: addEventMenuPos.width,
                        maxHeight: addEventMenuPos.maxHeight,
                      }
                    : { visibility: 'hidden' }
                }
              >
                {macroEventMenuGroups.map((group) => (
                  <div className="menu-group" key={group.title}>
                    <div className="menu-group-title">{group.title}</div>
                    {group.items.map((item) => (
                      <div
                        key={item.key}
                        className="add-event-item"
                        onClick={() => addMacroRecord(item.key)}
                      >
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* 字符面板 */}
        <div className="macro-panel macro-chars">
          <div className="section-title">
            字符
            {(selectedKeyIds.length > 0 ||
              (editingRecordId && macroRecords.some((r) => r.id === editingRecordId && r.type === 'key'))) && (
              <span className="char-listen-hint">也可直接按键盘</span>
            )}
          </div>
          <div className="char-grid">
            {macroBasicKeys.map((key, index) => {
              const canAssign =
                selectedKeyIds.length > 0 ||
                (editingRecordId != null &&
                  macroRecords.some((r) => r.id === editingRecordId && r.type === 'key'));
              return (
                <button
                  key={index}
                  className="char-btn"
                  onClick={() => setRecordKey(key.code, resolveCharPanelKeyName(key))}
                  disabled={!canAssign}
                  title={
                    !canAssign
                      ? '请先点击要赋值的按键记录'
                      : `写入按键：${resolveCharPanelKeyName(key)}`
                  }
                >
                  {key.label}
                </button>
              );
            })}
          </div>
        </div>

              </div>
              <div className="modal-footer macro-editor-footer">
                {macroIsNew && (
                  <button type="button" className="modal-btn modal-btn-cancel" onClick={() => setMacroWizardStep('name')}>
                    上一步
                  </button>
                )}
                {!macroWorkspace && (
                  <button type="button" className="modal-btn modal-btn-cancel" onClick={closeMacroEditor}>关闭</button>
                )}
                <button
                  ref={submitButtonRef}
                  type="button"
                  className="modal-btn modal-btn-submit macro-submit-btn-inline"
                  onClick={async () => { await submitMacro(); }}
                  disabled={!selectedMacro || macroRecords.length === 0}
                >
                  {editingMacro?.fromDevice ? '保存到设备' : '保存到本地'}
                </button>
              </div>
                </>
              )}
            </div>
          </div>,
          macroWorkspace
        )}

        {loopManagerMacroId && (
          <div className="modal-overlay" onClick={closeLoopManager}>
            <div className="modal-dialog macro-loop-manager-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">宏循环设置</span>
              </div>
              <div className="modal-body">
                <p className="macro-loop-manager-hint">
                  循环写在宏数据里，绑定该宏的按键都会使用这份设置。
                </p>
                {renderLoopModeOptions({
                  mode: managerLoopMode,
                  count: managerLoopCount,
                  onModeChange: setManagerLoopMode,
                  onCountChange: setManagerLoopCount,
                  countClassName: 'macro-manager-loop-count-input',
                  burstCountClassName: 'macro-manager-loop-burst-count-input',
                })}
              </div>
              <div className="modal-footer">
                <button type="button" className="modal-btn modal-btn-cancel" onClick={closeLoopManager}>
                  取消
                </button>
                <button
                  type="button"
                  className="modal-btn modal-btn-submit macro-loop-manager-submit"
                  onClick={applyLoopManager}
                >
                  应用
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 重命名弹窗 */}
        {editingMacroName && (
          <div className="modal-overlay" onClick={cancelRenameMacro}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">重命名宏</span>
              </div>
              <div className="modal-body">
                <input
                  type="text"
                  className="modal-input"
                  value={tempMacroName}
                  onChange={(e) => setTempMacroName(e.target.value)}
                  onKeyDown={(e) => handleMacroNameKeyDown(e, editingMacroName)}
                  placeholder="请输入宏名称"
                  autoFocus
                />
              </div>
              <div className="modal-footer">
                <button className="modal-btn modal-btn-cancel" onClick={cancelRenameMacro}>
                  取消
                </button>
                <button className="modal-btn modal-btn-submit" onClick={() => confirmRenameMacro(editingMacroName)}>
                  提交
                </button>
              </div>
            </div>
          </div>
        )}

        {/* 数据超限弹窗 */}
        {showDataOverflowModal && (
          <div className="modal-overlay" onClick={() => setShowDataOverflowModal(false)}>
            <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <span className="modal-title">数据超限</span>
              </div>
              <div className="modal-body">
                <p style={{ color: '#fff', margin: 0 }}>宏数据总大小超过 4096 字节限制，请减少宏记录数量后重试。</p>
              </div>
              <div className="modal-footer">
                <button className="modal-btn modal-btn-submit" onClick={() => setShowDataOverflowModal(false)}>
                  确定
                </button>
              </div>
            </div>
          </div>
        )}

      </>
    );
  };

// 命令模式：纵向分类列表（不含组合键/宏面板）
  const renderCommandCatalog = () => {
    const q = commandSearch.trim().toLowerCase();
    const currentValue = getSelectedButtonValue();
    const currentUpper = currentValue ? String(currentValue).toUpperCase() : null;

    const filtered = COMMAND_CATEGORIES
      .filter((cat) => cat.kind === 'commands')
      .map((cat) => {
        const commands = (cat.commands || []).filter(
          (cmd) =>
            !q ||
            matchesSearch(cmd.label, q) ||
            matchesSearch(cmd.keyLabel, q) ||
            matchesSearch(cmd.usage, q) ||
            matchesSearch(cmd.value, q) ||
            matchesSearch(cat.label, q)
        );
        if (q && commands.length === 0) return null;
        return { ...cat, commands };
      })
      .filter(Boolean);

    if (filtered.length === 0) {
      return <div className="cmd-empty">无匹配命令</div>;
    }

    return (
      <div className="cmd-catalog">
        {filtered.map((cat) => {
          const isOpen = q ? true : !!openCategories[cat.id];
          const hasCurrent = cat.commands.some(
            (cmd) =>
              currentUpper && String(cmd.value).toUpperCase() === currentUpper
          );
          return (
            <div
              key={cat.id}
              className={`cmd-category ${isOpen ? 'is-open' : ''} ${hasCurrent ? 'has-current' : ''}`}
            >
              <button
                type="button"
                className="cmd-category-toggle"
                aria-expanded={isOpen}
                onClick={() => toggleCategory(cat.id)}
              >
                <span className={`cmd-chevron ${isOpen ? 'open' : ''}`} aria-hidden>
                  ›
                </span>
                <span className="cmd-category-label">{cat.label}</span>
                {hasCurrent && !isOpen && (
                  <span className="cmd-category-dot" title="含当前绑定" />
                )}
                <span className="cmd-category-count">{cat.commands.length}</span>
              </button>
              {isOpen && (
                <div className="cmd-list" role="list">
                  {cat.commands.map((cmd) => {
                    const isCurrent =
                      currentUpper &&
                      String(cmd.value).toUpperCase() === currentUpper;
                    return (
                      <button
                        key={`${cat.id}-${cmd.value}-${cmd.label}`}
                        type="button"
                        role="listitem"
                        className={`cmd-item assign-draggable ${isCurrent ? 'current' : ''} ${
                          assignDrag?.kind === 'command' && assignDrag.value === cmd.value
                            ? 'is-dragging'
                            : ''
                        }`}
                        draggable
                        onDragStart={(event) =>
                          beginAssignDrag(event, {
                            kind: 'command',
                            value: cmd.value,
                            label: cmd.keyLabel || cmd.usage || cmd.label,
                          })
                        }
                        onDragEnd={endAssignDrag}
                        onClick={() => handleKeyMapButtonClick(cmd.value)}
                        title={`${cmd.keyLabel}：${cmd.usage}（${cmd.value}）· 可拖到鼠标按键上分配`}
                      >
                        <span className="cmd-item-key">{cmd.keyLabel}</span>
                        <span className="cmd-item-purpose">{cmd.usage}</span>
                        {isCurrent && <span className="cmd-item-badge">当前</span>}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderAssignBody = () => {
    if (assignMode === 'key') return renderCombinationGrid();
    if (assignMode === 'macro') return renderMacroGrid();
    return renderCommandCatalog();
  };

  const handleApplyDefault = () => {
    if (!selectedButton) {
      toast.warning(`请先在${previewSideLabel}选择鼠标按键`);
      return;
    }
    const def = BUTTON_DEFAULT_VALUES[selectedButton];
    if (!def) {
      toast.warning('该按键没有默认设置');
      return;
    }
    handleKeyMapButtonClick(def);
  };

  const handleApplyDisable = () => {
    if (!selectedButton) {
      toast.warning(`请先在${previewSideLabel}选择鼠标按键`);
      return;
    }
    handleKeyMapButtonClick('0x00000000');
  };

  const handlePreviewAreaClick = (event) => {
    if (!selectedButton) return;

    // 按键标签和设置控件仍按原逻辑工作；其余区域视为空白并退出选择。
    if (
      event.target.closest(
        '.location-label, .label-quick-actions, .macro-binding-panel, button, input, label'
      )
    ) {
      return;
    }

    setSelectedButton(null);
  };

  const renderMousePreview = () => {
    const selectedMapValue = selectedButton
      ? String(mouseLabels[selectedButton]?.value || '').toUpperCase()
      : '';
    const selectedDefault = selectedButton
      ? String(BUTTON_DEFAULT_VALUES[selectedButton] || '').toUpperCase()
      : '';
    const previewDefaultActive =
      !!selectedButton && !!selectedDefault && selectedMapValue === selectedDefault;
    const previewDisableActive =
      !!selectedButton &&
      (selectedMapValue === '0X00000000' || selectedMapValue === '0XFF000000');
    const wheelButtonKeys = ['middle', 'scrollUp', 'scrollDown'];
    const rightButtonPosition = mouseVisual?.labelPositions?.right;
    const wheelTrunkX = rightButtonPosition
      ? (rightButtonPosition.point.x + rightButtonPosition.label.x) / 2
      : 189;
    const selectedWheelPosition = wheelButtonKeys.includes(selectedButton)
      ? mouseVisual?.labelPositions?.[selectedButton]
      : null;

    return (
    <div
      className={`mouse-display-container ${assignDrag ? 'is-assign-dragging' : ''}`}
      style={{ '--mouse-preview-scale': mousePreviewScale }}
      onClick={handlePreviewAreaClick}
      onDragStart={(event) => event.preventDefault()}
      onDragOver={(event) => {
        // 允许在预览区内移动，避免浏览器把整块区域判定为禁止放置
        if (assignDrag) event.preventDefault();
      }}
      onDrop={(event) => {
        // 落在按键之外的空白处：不改任何映射，只收起提示
        if (!assignDrag) return;
        event.preventDefault();
        endAssignDrag();
      }}
    >
      {assignDrag && (
        <div className="assign-drop-hint">
          {assignDrag.kind === 'macro' && !assignDrag.onboard
            ? '本地宏需先导入板载宏，才能拖到按键上绑定'
            : `把「${assignDrag.label}」拖到要分配的鼠标按键上`}
        </div>
      )}
      <div className="mouse-preview-stage">
        <div className="img-view">
          {mouseVisual?.image && mouseVisual.imageCanvas ? (
            <svg
              className="mouse-product-image"
              viewBox={mouseVisual.viewBox}
              preserveAspectRatio="xMidYMid meet"
              role="img"
              aria-label={deviceInfo.productName || '当前鼠标'}
            >
              <image
                href={mouseVisual.image}
                width={mouseVisual.imageCanvas.width}
                height={mouseVisual.imageCanvas.height}
              />
            </svg>
          ) : mouseVisual?.image ? (
            <img
              className="mouse-product-image"
              src={mouseVisual.image}
              alt={deviceInfo.productName || '当前鼠标'}
              draggable="false"
            />
          ) : (
            <div className="mouse-silhouette" aria-hidden>
              <div className="ms-shell" />
            </div>
          )}

          {mouseVisual && (
            <svg
              className="mouse-button-zones"
              viewBox={mouseVisual.viewBox}
              preserveAspectRatio="xMidYMid meet"
              aria-label="鼠标按键区域"
            >
              {Object.entries(mouseVisual.buttonPaths).map(([key, path]) => (
                <path
                  key={key}
                  className={`mouse-button-zone ${
                    selectedButton === key ? 'is-selected' : ''
                  } ${buttonDropClass(key)}`}
                  d={path}
                  transform={mouseVisual.buttonTransform}
                  onClick={(event) => {
                    event.stopPropagation();
                    handlePreviewButtonClick(key);
                  }}
                  {...buttonDropHandlers(key)}
                >
                  <title>{MOUSE_BUTTON_LABELS[key]}</title>
                </path>
              ))}
            </svg>
          )}

          {Object.entries(mouseVisual?.labelPositions || {}).map(([key, pos]) => {
            const labelData = {
              name: MOUSE_BUTTON_LABELS[key],
              label: mouseLabels[key]?.label || MOUSE_BUTTON_LABELS[key],
            };

            const isSelected = selectedButton === key;
            const isWheelButton = wheelButtonKeys.includes(key);
            const showConnection = !isWheelButton || isSelected;
            const isMacroBinding = isMacroBindingValue(mouseLabels[key]?.value);
            const labelWidth = 128;
            // 引线接到标签内侧边
            const labelEdgeX =
              pos.direction === 'left'
                ? pos.label.x + labelWidth
                : pos.label.x;
            const midX = (pos.point.x + labelEdgeX) / 2;
            // 滚轮卡片在图片外共用一条主线。只有选中具体滚轮功能时，
            // 才从图片边缘延伸到对应热区，避免默认状态遮挡产品图。
            const connectionPath =
              isWheelButton && pos.direction === 'right'
                ? `M ${pos.point.x} ${pos.point.y} H ${wheelTrunkX}`
                : `M ${pos.point.x} ${pos.point.y} L ${midX} ${pos.point.y} L ${midX} ${pos.label.y} L ${labelEdgeX} ${pos.label.y}`;
            // 快捷操作贴在标签外侧，避免拉高卡片互相遮挡
            const actionsLeft =
              pos.direction === 'left'
                ? pos.label.x - 8
                : pos.label.x + labelWidth + 8;
            const actionsTransform =
              pos.direction === 'left'
                ? 'translate(-100%, -50%)'
                : 'translateY(-50%)';

            const dropClass = buttonDropClass(key);

            return (
              <div
                key={key}
                className={`guide-item ${isSelected ? 'is-selected' : ''} ${dropClass}`}
                style={{ zIndex: isSelected || dropClass ? 30 : 5 }}
                onClick={() => handlePreviewButtonClick(key)}
                {...buttonDropHandlers(key)}
              >
                <div
                  className={`location-label ${isSelected ? 'active' : ''} ${dropClass}`}
                  style={{
                    position: 'absolute',
                    left: pos.label.x,
                    top: pos.label.y,
                    transform: 'translateY(-50%)',
                    zIndex: isSelected ? 32 : 10,
                  }}
                >
                  <div className="label-name">{labelData.name}</div>
                  <div className="label-function">
                    <span className="label-function-text">{labelData.label}</span>
                    {isMacroBinding && (
                      <span className="label-function-type">宏</span>
                    )}
                  </div>
                </div>

                {isSelected && (
                  <div
                    className={`label-quick-actions float ${pos.direction}`}
                    style={{
                      position: 'absolute',
                      left: actionsLeft,
                      top: pos.label.y,
                      transform: actionsTransform,
                      zIndex: 40,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`label-quick-btn ${previewDefaultActive ? 'active' : ''}`}
                      onClick={handleApplyDefault}
                      disabled={!isConnected}
                      title="恢复该按键出厂默认功能"
                    >
                      默认设置
                    </button>
                    <button
                      type="button"
                      className={`label-quick-btn muted ${previewDisableActive ? 'active' : ''}`}
                      onClick={handleApplyDisable}
                      disabled={!isConnected}
                      title="禁用该按键"
                    >
                      禁用
                    </button>
                  </div>
                )}

                <svg
                  className="connection-line"
                  style={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'visible',
                    pointerEvents: 'none',
                    // 未选中时藏在鼠标图片后面，避免连线压住产品外观。
                    zIndex: isSelected ? 20 : 1,
                  }}
                >
                  {/* 肘形指引线：键位点 → 水平 → 标签 */}
                  {showConnection && (
                    <>
                      <path
                        className={`connection-path ${isSelected ? 'is-selected' : ''}`}
                        d={connectionPath}
                        fill="none"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <circle
                        className={`connection-point ${isSelected ? 'is-selected' : ''}`}
                        cx={pos.point.x}
                        cy={pos.point.y}
                        r={isSelected ? 3.5 : 2.5}
                      />
                    </>
                  )}
                </svg>
              </div>
            );
          })}

          {mouseVisual?.labelPositions?.middle &&
            mouseVisual?.labelPositions?.scrollUp &&
            mouseVisual?.labelPositions?.scrollDown && (
              <svg
                className="connection-line wheel-shared-connection"
                style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  width: '100%',
                  height: '100%',
                  overflow: 'visible',
                  pointerEvents: 'none',
                  zIndex: selectedWheelPosition ? 20 : 1,
                }}
                aria-hidden="true"
              >
                {/* 三个滚轮卡片在图片外汇合，公共线止于鼠标图片边缘。 */}
                <path
                  className="connection-path"
                  d={`M ${wheelTrunkX} 67 V ${mouseVisual.labelPositions.scrollDown.label.y}
                      M ${wheelTrunkX} ${mouseVisual.labelPositions.middle.label.y} H ${mouseVisual.labelPositions.middle.label.x}
                      M ${wheelTrunkX} ${mouseVisual.labelPositions.scrollUp.label.y} H ${mouseVisual.labelPositions.scrollUp.label.x}
                      M ${wheelTrunkX} ${mouseVisual.labelPositions.scrollDown.label.y} H ${mouseVisual.labelPositions.scrollDown.label.x}`}
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {selectedWheelPosition && (
                  <path
                    className="connection-path is-selected"
                    d={`M ${wheelTrunkX} ${selectedWheelPosition.point.y} V ${selectedWheelPosition.label.y} H ${selectedWheelPosition.label.x}`}
                    fill="none"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                )}
              </svg>
            )}
        </div>
      </div>

      {(() => {
        const macroInfo = getSelectedButtonMacroInfo();
        if (!macroInfo) return null;
        const hasChanges =
          bindingLoopMode !== macroInfo.loopMode ||
          (macroLoopModeUsesCount(bindingLoopMode) &&
            bindingLoopCount !== macroInfo.loopCount);

        return (
          <div className="macro-binding-panel">
            <div className="macro-binding-title">宏绑定设置</div>
            <div className="macro-binding-info">
              <span className="macro-binding-label">绑定宏:</span>
              <span className="macro-binding-value">{macroInfo.macroName}</span>
            </div>
            <div className="macro-binding-loop">
              <div className="macro-binding-label">循环设置:</div>
              {renderLoopModeOptions({
                mode: bindingLoopMode,
                count: bindingLoopCount,
                onModeChange: setBindingLoopMode,
                onCountChange: setBindingLoopCount,
              })}
            </div>
            <button
              className={`macro-binding-submit ${hasChanges ? 'has-changes' : ''}`}
              onClick={() => updateSelectedButtonMacroLoop(bindingLoopMode, bindingLoopCount)}
              disabled={!hasChanges}
            >
              应用
            </button>
          </div>
        );
      })()}
    </div>
    );
  };

  const selectedLabel = selectedButton
    ? (
        {
          left: '左键',
          right: '右键',
          middle: '中键',
          scrollUp: '滚轮上',
          scrollDown: '滚轮下',
          button4: '侧键4',
          button5: '侧键5',
          dpi: 'DPI',
        }[selectedButton] || selectedButton
      )
    : null;

  if (macroWorkspace) {
    return (
      <div className="key-function macro-workspace-mode">
        {renderMacroGrid()}
        {!macroEditorOpen && !isWorkspaceEditorVisible && (
          <div className="macro-workspace-empty">
            <div className="macro-workspace-empty-icon" aria-hidden>⌘</div>
            <strong>{macros.length > 0 ? '请选择宏' : '暂无宏'}</strong>
            <span>
              {macros.length > 0
                ? '从左侧列表选择一个宏进行编辑'
                : '点击左侧“新建本地宏”开始创建'}
            </span>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="key-function">
      <div className={`key-function-main ${isPreviewFirst ? 'preview-first' : ''}`}>
        {/* 按键分配 */}
        <section className="assign-panel">
          <div className="assign-header">
            <div className="assign-title-row">
              <div className="assign-heading">
                <h2>按键分配</h2>
                <button
                  type="button"
                  className="assign-layout-switch"
                  aria-label={
                    isPreviewFirst
                      ? '将按键分配面板移到左侧'
                      : '将按键分配面板移到右侧'
                  }
                  data-tooltip={
                    isPreviewFirst
                      ? '将按键分配面板移到左侧'
                      : '将按键分配面板移到右侧'
                  }
                  onClick={() => {
                    const nextPreviewFirst = !isPreviewFirst;
                    setIsPreviewFirst(nextPreviewFirst);
                    writePanelOrder(
                      KEY_FUNCTION_PANEL_ORDER_KEY,
                      nextPreviewFirst ? 'preview-first' : 'assign-first'
                    );
                  }}
                >
                  <ChevronIcon direction={isPreviewFirst ? 'left' : 'right'} />
                </button>
              </div>
              <div className="assign-title-meta">
                {selectedLabel ? (
                  <span className="assign-target">
                    目标：<strong>{selectedLabel}</strong>
                    <em>{mouseLabels[selectedButton]?.label || '—'}</em>
                  </span>
                ) : (
                  <span className="assign-target muted">
                    在{previewSideLabel}选择鼠标按键，或把下方条目直接拖到按键上
                  </span>
                )}
              </div>
            </div>

            <div className="assign-toolbar">
              <div className="assign-mode-tabs">
                {[
                  { id: 'command', label: '命令' },
                  { id: 'key', label: '按键' },
                  { id: 'macro', label: '宏' },
                ].map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    className={`assign-mode-tab ${assignMode === m.id ? 'active' : ''}`}
                    onClick={() => {
                      setAssignMode(m.id);
                      setCommandSearch('');
                      if (m.id !== 'macro') {
                        if (isMacroRecording) setIsMacroRecording(false);
                        setMacroEditorOpen(false);
                      }
                      if (m.id === 'key') setActiveTab('combination');
                      if (m.id === 'macro') setActiveTab('macro');
                      if (m.id === 'command') setActiveTab('basic');
                    }}
                  >
                    {m.label}
                  </button>
                ))}
              </div>

              {(assignMode === 'command' || assignMode === 'key') && (
                <div className="cmd-search">
                  <span className="cmd-search-icon" aria-hidden />
                  <input
                    type="search"
                    placeholder={assignMode === 'key' ? '搜索按键…' : '搜索命令…'}
                    value={commandSearch}
                    onChange={(e) => setCommandSearch(e.target.value)}
                  />
                  {commandSearch && (
                    <button
                      type="button"
                      className="cmd-search-clear"
                      onClick={() => setCommandSearch('')}
                      aria-label="清除搜索"
                    >
                      ×
                    </button>
                  )}
                </div>
              )}

              <label className="auto-jump-toggle">
                <input
                  type="checkbox"
                  checked={autoJumpToTemplate}
                  onChange={(e) => setAutoJumpToTemplate(e.target.checked)}
                />
                <span className="toggle-label">自动定位</span>
              </label>
            </div>
          </div>

          <div className="assign-body">{renderAssignBody()}</div>
        </section>

        {/* 鼠标预览 */}
        <aside ref={mousePreviewPaneRef} className="mouse-preview-pane">
          {renderMousePreview()}
        </aside>
      </div>
    </div>
  );
};

export default KeyFunction;
